/* ==========================================================================
   La Mior - Shade Finder
   --------------------------------------------------------------------------
   Architecture note, because it drives most of the decisions below:

   The COLOUR MATCH IS DETERMINISTIC. Skin colour is sampled from the photo,
   converted to CIELAB, and compared against each shade with CIEDE2000. That
   maths is exact, instant, free, and runs entirely on the customer's device.

   The LLM is NOT used to pick the shade. It is used for the things a model is
   actually good at and maths is not:
     - judging whether the photo is usable (lighting, filters, existing makeup)
     - reconciling a photo result that disagrees with the quiz answers
     - writing the recommendation copy in the brand's voice

   This matters for three reasons:
     1. Accuracy - ΔE2000 against measured Lab beats an LLM eyeballing a photo.
     2. Cost - see docs/COST-ESTIMATE.md. We send ~250x64px of skin patches,
        not a full selfie, so image tokens are negligible.
     3. Reliability - if the API is down, refuses, or the store is offline,
        the finder still works. The network call is an enhancement, never a
        dependency.

   We also never send a recognisable face to the model. Only small crops of
   skin are transmitted for analysis. The full photo (if consented) goes
   straight to R2 for the client's own review and is never sent to OpenAI.
   ========================================================================== */

(function () {
  'use strict';

  // ==========================================================================
  // Configuration
  // ==========================================================================

  const MEDIAPIPE = {
    // Pinned version - do not float to @latest, the API has broken between minors before.
    module: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/vision_bundle.mjs',
    wasm: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm',
    model:
      'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
    // To self-host (recommended for production - removes a third-party runtime
    // dependency and any CSP friction): upload vision_bundle.mjs, the wasm
    // folder and the .task model to Shopify Files, then swap these three URLs.
  };

  // Face-mesh landmark indices used for sampling. These are stable across
  // MediaPipe versions for the 468-point mesh.
  const REGIONS = {
    forehead: [10, 108, 337, 9, 151, 67, 297],
    leftCheek: [205, 206, 216, 123, 117, 118],
    rightCheek: [425, 426, 436, 352, 346, 347],
    jaw: [152, 199, 175, 200, 421, 201],
  };

  /*
    No exposure correction, and no white balance.

    Both used to be anchored on the whites of the eyes, which meant no answer
    for anyone smiling hard, wearing glasses, or turned away from the camera.
    The obvious replacement, the brightest thing in the frame, does not survive
    contact with real photographs: measured across this store's own shots, the
    top of the histogram sits at pure white in four out of five, because a
    bright wall or a window clips. An anchor that reads 100 whatever the light
    is doing carries no information, and worse, it would quietly darken every
    photo taken against a white wall.

    So the finder no longer pretends to know how bright the room was. It reads
    the skin as photographed, which is reliable for ranking shades against each
    other, and names a shade from it.

    What we can still measure honestly is when a photo is too dark, too blown
    out or too unevenly lit to trust. That does not buy a refusal and it does
    not buy a swatch screen: it buys one line of notice beside the match saying
    the light was low and which way that pushes the reading, printed directly
    above the one-tap correction that answers it.
  */
  const DARK_FRAME_L = 32;
  const BLOWN_REGION_L = 92;
  const CRUSHED_REGION_L = 18;

  /*
    A region receiving less than this share of the light that falls on the
    brightest region is in shadow, not a different colour of skin. Shadow only
    ever subtracts light, so averaging it in can only drag the reading deeper.

    The test is a ratio of linear luminance rather than a gap in L*, because
    shadow is multiplicative: a fixed L* gap picks different regions at
    different exposures, which made the answer wobble by three L* as the room
    dimmed. On the reference face 0.7 keeps the jaw and drops the cheek that
    sits in the shade of the nose, at every exposure tested.
  */
  const SHADOW_LUMINANCE_RATIO = 0.7;

  /*
    How unevenly a face may be lit before the reading stops being trustworthy.

    Measured as the interquartile spread of the skin patches, in L*. Properly lit
    photography runs 4 to 11; a face half in shadow runs past 20.

    This used to refuse the photo outright above a threshold. It does not any
    more. A customer who has taken a photo wants an answer from it, and the
    swatch check after the reading is a better safety net than a locked door:
    she sees what we read, and one tap moves it. Poor light now costs confidence
    and earns a warning, not a refusal.
  */
  const UNCERTAIN_LIGHT_SPREAD_L = 14;

  /*
    How much room to leave above the finder when a new step scrolls into view.
    Prestige's header is sticky and about 70px tall, so anchoring flush to the
    top of the widget hides the heading behind it.
  */
  const SCROLL_HEADROOM = 90;

  const PATCH_RADIUS = 5; // px half-width of each sample square
  const MAX_UPLOAD_EDGE = 640; // stored photo longest edge
  const PATCH_STRIP_SIZE = 64; // each crop in the strip sent to the API

  // ==========================================================================
  // Built-in fallback content
  // --------------------------------------------------------------------------
  // The finder reads its questions and shades from Shopify metaobjects. If none
  // exist yet, it falls back to these so the page works the moment the theme
  // files are uploaded - no Shopify setup required to see it running.
  //
  // Anything created in Shopify Admin (Content > Metaobjects) overrides this
  // entirely. Treat these as the starting point, not the source of truth.
  // ==========================================================================

  const DEFAULT_QUESTIONS = [
    /*
      The six swatch colours are measured, not chosen.

      Their L* ladder is unchanged - five even steps across the product range,
      then a deliberate jump for "deeper than all of these". What changed is the
      hue and chroma at each step, which used to be picked by eye and were about
      ten units of chroma too grey. Held against real skin they read as dusty
      paint chips, and a customer comparing her jaw against a dusty chip picks
      the wrong one.

      The replacement values come from measuring 67 faces on a professionally
      lit reference ladder spanning L* 20.7 to 84.7, through this file's own
      region and patch code, sampling forehead and jaw only - blush and bronzer
      live on the cheeks and were pulling a* orange. a* and b* are then a
      distance-weighted MEDIAN against L*, not a mean, so the three faces shot
      under a warm gel cannot drag the whole locus with them.

      No photograph was copied to get these. A colour measured off an image is a
      fact about skin, not the photographer's picture, and nothing but the six
      numbers below came across.

      These are the ANSWER swatches. The shade table further down carries its own
      swatchHex values that describe the foundations and drive the colour match -
      do not "make them consistent" with these.
    */
    {
      key: 'depth', position: 1, layout: 'swatch', inputType: 'single',
      title: 'Which one looks most like your skin?',
      subtitle: 'Hold your phone next to your jaw, near a window. Pick the closest.',
      options: [
        { label: 'Fair', value: 'fair', helper: 'Very light. Burns in the sun.', swatchHex: '#CFA184', scores: { depth: 1 } },
        { label: 'Light', value: 'light', helper: 'Burns first, then tans.', swatchHex: '#C8946D', scores: { depth: 2 } },
        { label: 'Medium', value: 'medium', helper: 'Tans slowly over summer.', swatchHex: '#C6865D', scores: { depth: 3 } },
        { label: 'Medium-Tan', value: 'medium-tan', helper: 'Tans easily. Rarely burns.', swatchHex: '#AF7651', scores: { depth: 4 } },
        { label: 'Tan', value: 'tan', helper: 'Deep. Never really burns.', swatchHex: '#9B6448', scores: { depth: 5 } },
        { label: 'Deeper than all of these', value: 'deeper', helper: 'We will be honest with you.', swatchHex: '#8E4E2D', scores: { depth: 6, out_of_range: true } },
      ],
    },
    {
      key: 'undertone', position: 2, layout: 'card', inputType: 'single',
      title: 'Turn your wrist over. What colour do your veins look?',
      subtitle: 'Do this near a window. Indoor bulbs change the colour.',
      /*
        The "I cannot tell" answer used to sit in this list and was removed at
        the client's request. That makes this help text load-bearing rather than
        decorative: it is now the only way out for someone who genuinely cannot
        read her veins, and without it she is forced to guess, which puts noise
        straight into the undertone signal. If you trim it, put the fourth
        answer back.
      */
      helpText:
        'Hard to tell? Use jewellery instead.\n\nGold tends to suit warm skin. Silver tends to suit cool skin. If both look fine on you, pick "a bit of both".',
      options: [
        { label: 'Blue or purple', value: 'cool', helper: 'This means cool', swatchHex: '#8E97C1', scores: { undertone: 'cool' } },
        { label: 'Green', value: 'warm', helper: 'This means warm', swatchHex: '#B1BA7B', scores: { undertone: 'warm' } },
        { label: 'A bit of both', value: 'neutral', helper: 'This means neutral', swatchHex: '#A0A99D', scores: { undertone: 'neutral' } },
        /*
          Present in the data, hidden by default.

          The client wanted this answer gone, and it is - "Show the I cannot
          tell answer" in the theme editor is off out of the box. It stays in
          the data rather than being deleted so that turning it back on is a
          checkbox rather than a developer, because it is the only honest answer
          for a customer who cannot read her veins, and the day someone notices
          the undertone numbers are noisy this is the first thing to try.
        */
        { label: 'I cannot tell', value: 'unsure', helper: 'That is fine. We will use your other answers.', swatchHex: '#D6CEC6', scores: { undertone: 'unknown' } },
      ],
    },
    {
      key: 'coverage', position: 3, layout: 'card', inputType: 'single',
      title: 'How much do you want it to cover?',
      options: [
        { label: 'Just a little', value: 'sheer', helper: 'Evens things out. Still looks like your skin.', scores: { coverage: 'sheer' } },
        { label: 'A medium amount', value: 'medium', helper: 'Covers most marks. Still feels light.', scores: { coverage: 'medium' } },
        { label: 'Full cover', value: 'full', helper: 'Covers everything.', scores: { coverage: 'full' } },
        { label: 'Not sure', value: 'any', helper: 'Show me the best match.', scores: { coverage: 'any' } },
      ],
    },
    {
      key: 'concern', position: 4, layout: 'list', inputType: 'multi', optional: true,
      title: 'Anything you want it to help with?',
      subtitle: 'Pick as many as you like, or skip this one.',
      options: [
        { label: 'Dark circles', value: 'dark_circles', scores: {} },
        { label: 'Redness', value: 'redness', scores: {} },
        { label: 'Dull skin', value: 'dullness', scores: {} },
        { label: 'Large pores', value: 'texture', scores: {} },
        { label: 'Oily shine', value: 'oil', scores: {} },
      ],
    },
  ];

  /*
    CALIBRATED ESTIMATES, NOT MEASUREMENTS.

    The first version of this file converted marketing swatch hex codes to Lab.
    Those swatches are shot on white, so every value came out roughly 10 L*
    too light. The effect was that real skin looked darker than the whole
    range and almost everyone was told "Tan".

    These values instead place a five step warm range across L* 72 down to 45,
    which is where a foundation range of this size actually sits, with even
    steps of 6 to 7 L* and a* / b* drifting warmer as it deepens. The hex codes
    are generated from the Lab values, so the swatch you see and the number the
    matcher uses are the same colour.

    This is a big improvement on the old values but it is still an estimate.
    Real measurements need a colour card. See docs/SHADE-DATA.md.
  */
  /**
   * Lab -> hex, for generating a swatch from an estimated colour.
   *
   * Only used to give the built-in shades something to render before anyone
   * fills in a real swatch. It is the inverse of rgbToLab below, kept here
   * rather than beside it because DEFAULT_SHADES is built at load time and
   * needs it first.
   */
  function labToHexApprox(L, a, b) {
    const fy = (L + 16) / 116;
    const fx = fy + a / 500;
    const fz = fy - b / 200;
    const inv = (t) => (t * t * t > 0.008856 ? t * t * t : (t - 16 / 116) / 7.787);
    const X = inv(fx) * 0.95047;
    const Y = inv(fy);
    const Z = inv(fz) * 1.08883;
    const lin = [
      X * 3.2404542 + Y * -1.5371385 + Z * -0.4985314,
      X * -0.9692660 + Y * 1.8760108 + Z * 0.0415560,
      X * 0.0556434 + Y * -0.2040259 + Z * 1.0572252,
    ];
    const enc = (v) => {
      const c = Math.max(0, Math.min(1, v));
      const srgb = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
      return Math.round(srgb * 255).toString(16).padStart(2, '0');
    };
    return '#' + lin.map(enc).join('').toUpperCase();
  }

  /*
    The built-in shade tables, one per product the finder covers.

    Read the warning in HANDOVER.md before trusting the lab values: they are
    ESTIMATES derived from each shade's swatch, not measurements of the product.
    They are good enough to rank shades against each other, which is all the
    matcher asks of them, and not good enough to argue with a customer about.
    Measuring a real colour card replaces them.

    Live variant ids are filled in so "Add to bag" works before anyone creates
    the metaobjects. Metaobjects win the moment they exist.
  */

  /* Glow Burst: five depths, no undertone axis. */
  const GLOW_BURST = [
    { handle: 'gb-fair',       name: 'Fair',       swatchHex: '#CDAA91', depthIndex: 1, undertone: 'neutral', lab: { L: 72, a: 9.5,  b: 17.5 }, variantId: 43333697503414 },
    { handle: 'gb-light',      name: 'Light',      swatchHex: '#BF997E', depthIndex: 2, undertone: 'warm',    lab: { L: 66, a: 10.5, b: 19.5 }, variantId: 43333697536182 },
    { handle: 'gb-medium',     name: 'Medium',     swatchHex: '#AF8569', depthIndex: 3, undertone: 'warm',    lab: { L: 59, a: 12,   b: 21.5 }, variantId: 43333697568950 },
    { handle: 'gb-medium-tan', name: 'Medium-Tan', swatchHex: '#9E7355', depthIndex: 4, undertone: 'warm',    lab: { L: 52, a: 13,   b: 23 },   variantId: 43333697601718 },
    { handle: 'gb-tan',        name: 'Tan',        swatchHex: '#8B6144', depthIndex: 5, undertone: 'warm',    lab: { L: 45, a: 13.5, b: 23.5 }, variantId: 43333697634486 },
  ].map((sh) => ({
    ...sh,
    family: 'foundation',
    headline: sh.name,
    description:
      'Glow Burst Encapsulated Foundation in ' + sh.name +
      '.\n\nStuck between two shades? Take the lighter one. It is much easier to warm up with bronzer than to fix a shade that sits too dark.',
    ctaLabel: 'Add to bag',
    image: null,
    variantTitle: sh.name,
    price: 109500,
    available: true,
    productUrl: '/products/glow-burst-encapsulated-foundation',
    productHandle: 'glow-burst-encapsulated-foundation',
    productTitle: 'Glow Burst Encapsulated Foundation',
    alternateHandle: null,
  }));

  /*
    Daily Sunbath: six depths crossed with three undertones, eighteen shades.

    Two things follow from that shape. Its depth 6 is a REAL shade - "Deep" -
    not the out-of-range answer it is for Glow Burst, which the question copy
    adapts to below. And because undertone is a genuine axis here rather than a
    tiebreak, the a* and b* estimates move with it: cool leans less yellow, warm
    more, neutral between.
  */
  const SUNBATH_DEPTHS = [
    { key: 'fair',          name: 'Fair',          code: '1', depth: 1, L: 72 },
    { key: 'light',         name: 'Light',         code: '2', depth: 2, L: 66 },
    { key: 'light-medium',  name: 'Light-Medium',  code: '3', depth: 3, L: 59 },
    { key: 'medium',        name: 'Medium',        code: '4', depth: 4, L: 52 },
    { key: 'tan',           name: 'Tan',           code: '5', depth: 5, L: 45 },
    { key: 'deep',          name: 'Deep',          code: '6', depth: 6, L: 38 },
  ];
  const SUNBATH_TONES = [
    { key: 'cool',    name: 'Cool',    suffix: 'C', a: 11,   b: 15.5 },
    { key: 'warm',    name: 'Warm',    suffix: 'W', a: 12.5, b: 24 },
    { key: 'neutral', name: 'Neutral', suffix: 'N', a: 11.5, b: 20 },
  ];
  const SUNBATH_VARIANTS = {
    '100C': 50499864789174, '110W': 50499864821942, '120N': 50499864854710,
    '200C': 50499864887478, '210W': 50499864920246, '220N': 50499864953014,
    '300C': 50499864985782, '310W': 50499865018550, '320N': 50499865051318,
    '400C': 50499865084086, '410W': 50499865116854, '420N': 50499865149622,
    '500C': 50499865182390, '510W': 50499865215158, '520N': 50499865247926,
    '600C': 50499865280694, '610W': 50499865313462, '630N': 50499865346230,
  };

  const DAILY_SUNBATH = [];
  SUNBATH_DEPTHS.forEach((d) => {
    SUNBATH_TONES.forEach((t, i) => {
      // The shade codes run 100/110/120, 200/210/220 ... with one exception at
      // the deep end, where neutral is 630 rather than 620. Taken from the live
      // product rather than assumed, because a wrong id silently breaks the cart.
      let code = d.code + String(i) + '0' + t.suffix;
      if (!SUNBATH_VARIANTS[code] && SUNBATH_VARIANTS[d.code + '30' + t.suffix]) {
        code = d.code + '30' + t.suffix;
      }
      const variantTitle = d.name + ' ' + t.name + ' - ' + code;
      DAILY_SUNBATH.push({
        handle: 'ds-' + d.key + '-' + t.key,
        name: d.name + ' ' + t.name,
        swatchHex: labToHexApprox(d.L, t.a, t.b),
        depthIndex: d.depth,
        undertone: t.key,
        lab: { L: d.L, a: t.a, b: t.b },
        family: 'foundation',
        headline: d.name + ' ' + t.name,
        description:
          'Daily Sunbath Fluid Skin Tint SPF 50 in ' + variantTitle +
          '.\n\nBetween two depths? Take the lighter one - bronzer fixes it. Between two undertones? Neutral is the safer miss.',
        ctaLabel: 'Add to bag',
        image: null,
        variantId: SUNBATH_VARIANTS[code] || null,
        variantTitle,
        price: 79500,
        available: true,
        productUrl: '/products/daily-sunbath-fluid-skin-tint-spf-50',
        productHandle: 'daily-sunbath-fluid-skin-tint-spf-50',
        productTitle: 'Daily Sunbath Fluid Skin Tint SPF 50',
        alternateHandle: null,
      });
    });
  });

  const DEFAULT_SHADES = GLOW_BURST.concat(DAILY_SUNBATH);

  // ==========================================================================
  // Colour science
  // ==========================================================================

  /** sRGB (0-255) -> linear RGB (0-1) */
  function srgbToLinear(c) {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }

  function linearToSrgb(v) {
    const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, c * 255));
  }

  /** L* -> relative luminance Y, so exposure can be scaled in linear light. */
  function labLToY(L) {
    const fy = (L + 16) / 116;
    return fy > 6 / 29 ? fy * fy * fy : 3 * (6 / 29) * (6 / 29) * (fy - 4 / 29);
  }

  /** sRGB (0-255) -> CIELAB, D65 illuminant / 2 degree observer */
  function rgbToLab(r, g, b) {
    const R = srgbToLinear(r);
    const G = srgbToLinear(g);
    const B = srgbToLinear(b);

    // Linear RGB -> XYZ, then normalise by D65 white point
    let X = (R * 0.4124564 + G * 0.3575761 + B * 0.1804375) / 0.95047;
    let Y = R * 0.2126729 + G * 0.7151522 + B * 0.072175;
    let Z = (R * 0.0193339 + G * 0.119192 + B * 0.9503041) / 1.08883;

    const f = (t) => (t > 0.008856451679 ? Math.cbrt(t) : 7.787037 * t + 16 / 116);
    const fx = f(X);
    const fy = f(Y);
    const fz = f(Z);

    return {
      L: 116 * fy - 16,
      a: 500 * (fx - fy),
      b: 200 * (fy - fz),
    };
  }

  /**
   * CIEDE2000 colour difference.
   * Worth the extra ~40 lines over ΔE76: it is far better behaved in the
   * warm mid-lightness region where every foundation shade lives, which is
   * exactly where ΔE76 exaggerates hue differences.
   */
  function deltaE2000(lab1, lab2) {
    const kL = 1, kC = 1, kH = 1;
    const deg = Math.PI / 180;

    const C1 = Math.hypot(lab1.a, lab1.b);
    const C2 = Math.hypot(lab2.a, lab2.b);
    const Cbar = (C1 + C2) / 2;

    const Cbar7 = Math.pow(Cbar, 7);
    const G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + Math.pow(25, 7))));

    const a1p = (1 + G) * lab1.a;
    const a2p = (1 + G) * lab2.a;

    const C1p = Math.hypot(a1p, lab1.b);
    const C2p = Math.hypot(a2p, lab2.b);

    const h1p = Math.atan2(lab1.b, a1p) < 0
      ? Math.atan2(lab1.b, a1p) + 2 * Math.PI
      : Math.atan2(lab1.b, a1p);
    const h2p = Math.atan2(lab2.b, a2p) < 0
      ? Math.atan2(lab2.b, a2p) + 2 * Math.PI
      : Math.atan2(lab2.b, a2p);

    const dLp = lab2.L - lab1.L;
    const dCp = C2p - C1p;

    let dhp;
    if (C1p * C2p === 0) dhp = 0;
    else if (Math.abs(h2p - h1p) <= Math.PI) dhp = h2p - h1p;
    else if (h2p - h1p > Math.PI) dhp = h2p - h1p - 2 * Math.PI;
    else dhp = h2p - h1p + 2 * Math.PI;

    const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(dhp / 2);

    const Lbarp = (lab1.L + lab2.L) / 2;
    const Cbarp = (C1p + C2p) / 2;

    let hbarp;
    if (C1p * C2p === 0) hbarp = h1p + h2p;
    else if (Math.abs(h1p - h2p) <= Math.PI) hbarp = (h1p + h2p) / 2;
    else if (h1p + h2p < 2 * Math.PI) hbarp = (h1p + h2p + 2 * Math.PI) / 2;
    else hbarp = (h1p + h2p - 2 * Math.PI) / 2;

    const T =
      1 -
      0.17 * Math.cos(hbarp - 30 * deg) +
      0.24 * Math.cos(2 * hbarp) +
      0.32 * Math.cos(3 * hbarp + 6 * deg) -
      0.20 * Math.cos(4 * hbarp - 63 * deg);

    const dTheta = 30 * deg * Math.exp(-Math.pow((hbarp / deg - 275) / 25, 2));
    const Cbarp7 = Math.pow(Cbarp, 7);
    const Rc = 2 * Math.sqrt(Cbarp7 / (Cbarp7 + Math.pow(25, 7)));
    const Sl =
      1 + (0.015 * Math.pow(Lbarp - 50, 2)) / Math.sqrt(20 + Math.pow(Lbarp - 50, 2));
    const Sc = 1 + 0.045 * Cbarp;
    const Sh = 1 + 0.015 * Cbarp * T;
    const Rt = -Math.sin(2 * dTheta) * Rc;

    return Math.sqrt(
      Math.pow(dLp / (kL * Sl), 2) +
        Math.pow(dCp / (kC * Sc), 2) +
        Math.pow(dHp / (kH * Sh), 2) +
        Rt * (dCp / (kC * Sc)) * (dHp / (kH * Sh))
    );
  }

  function hexToRgb(hex) {
    if (!hex) return null;
    const clean = String(hex).replace('#', '').trim();
    if (clean.length !== 6) return null;
    const n = parseInt(clean, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  // ==========================================================================
  // Pixel sampling
  // ==========================================================================

  /** Median of a numeric array. Robust against specular highlights and stray hair. */
  function median(values) {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  /**
   * Sample a square patch of pixels and return the per-channel median plus a
   * variance figure. High variance means we probably caught hair, a shadow
   * edge or a nostril rather than clean skin - the caller uses it to drop
   * unreliable regions.
   */
  function samplePatch(imageData, cx, cy, radius, opts = {}) {
    const { width, height, data } = imageData;
    const rs = [], gs = [], bs = [];

    const x0 = Math.max(0, Math.round(cx) - radius);
    const x1 = Math.min(width - 1, Math.round(cx) + radius);
    const y0 = Math.max(0, Math.round(cy) - radius);
    const y1 = Math.min(height - 1, Math.round(cy) + radius);

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const i = (y * width + x) * 4;
        if (data[i + 3] < 200) continue; // skip transparent
        rs.push(data[i]);
        gs.push(data[i + 1]);
        bs.push(data[i + 2]);
      }
    }

    if (!rs.length) return null;

    const lumOf = (i) => 0.299 * rs[i] + 0.587 * gs[i] + 0.114 * bs[i];
    const lum = rs.map((_, i) => lumOf(i));
    const meanLum = lum.reduce((s, v) => s + v, 0) / lum.length;
    const variance = lum.reduce((s, v) => s + (v - meanLum) ** 2, 0) / lum.length;

    /*
      brightestFraction narrows the sample to the lightest slice of the patch.
      Used for the sclera, where the surrounding lashes and lid would otherwise
      drag the median down and make the white of the eye look grey.
    */
    if (opts.brightestFraction) {
      const order = lum.map((v, i) => [v, i]).sort((x, y) => y[0] - x[0]);
      const take = Math.max(3, Math.round(order.length * opts.brightestFraction));
      const idx = order.slice(0, take).map((x) => x[1]);
      return {
        r: median(idx.map((i) => rs[i])),
        g: median(idx.map((i) => gs[i])),
        b: median(idx.map((i) => bs[i])),
        variance,
        samples: idx.length,
      };
    }

    return { r: median(rs), g: median(gs), b: median(bs), variance, samples: rs.length };
  }

  /**
   * Does this patch look like skin at all?
   *
   * Face meshes are fitted to a face, not clipped to it. Turn your head far
   * enough and a cheek landmark sits on the wall behind you, and the finder
   * would happily average that wall into your skin tone. Skin of every depth
   * has more red than green and more green than blue, and sits in a band of
   * colourfulness that neutral backgrounds and clothing fall outside of.
   */
  function looksLikeSkin(patch, dim) {
    const { r, g, b } = patch;
    if (!(r > g && g > b)) return false;
    const lab = rgbToLab(r, g, b);
    const chroma = Math.sqrt(lab.a * lab.a + lab.b * lab.b);

    /*
      In a frame the camera has already told us is dark, the floors come down.

      Skin under a dim bulb keeps the r > g > b ordering that identifies it and
      loses the saturation and lightness that the normal floors assume. Holding
      the daylight floors there is what produced "we could not get a clean read
      on your skin" for photos that were perfectly readable, just dark.
    */
    if (dim) return chroma >= 3 && chroma <= 60 && lab.L >= 3 && lab.L <= 95;
    /*
      Below the chroma floor is a grey wall or a white shirt; above the ceiling
      is lipstick, a saturated backdrop or a blown red channel.

      The floors are deliberately generous at the dark end. Skin photographed in
      a dim room loses both lightness and colourfulness, and a tighter test threw
      those patches away, which turned a low-light photo into "we could not get a
      clean read on your skin" rather than into an answer.
    */
    return chroma >= 7 && chroma <= 60 && lab.L >= 8 && lab.L <= 95;
  }

  /*
    Gains tried, in order, when the first look finds no face.

    Two passes rather than one: 2.2x rescues an ordinary indoor evening photo,
    and 4x rescues one taken with the lights basically off. Anything beyond that
    is amplifying sensor noise into shapes and would start inventing faces.
  */
  const DETECTION_GAINS = [2.2, 4];

  /**
   * A brightened copy of the frame, for the face detector only.
   *
   * The gain is applied in linear light rather than straight onto the 8-bit
   * values, so a dark frame opens up without the midtones tearing, and the
   * result is clamped rather than wrapped. Nothing measured ever comes from
   * this canvas - it exists purely so the landmarker has edges to find.
   */
  function liftForDetection(imageData, gain) {
    try {
      const { width, height, data } = imageData;
      const out = new Uint8ClampedArray(data.length);

      // Cached because this runs over every pixel of a 900px frame, twice.
      const lut = new Uint8ClampedArray(256);
      for (let v = 0; v < 256; v += 1) {
        const lin = v / 255 <= 0.04045 ? v / 255 / 12.92 : Math.pow((v / 255 + 0.055) / 1.055, 2.4);
        const lifted = Math.min(1, lin * gain);
        const enc = lifted <= 0.0031308 ? lifted * 12.92 : 1.055 * Math.pow(lifted, 1 / 2.4) - 0.055;
        lut[v] = Math.round(enc * 255);
      }

      for (let i = 0; i < data.length; i += 4) {
        out[i] = lut[data[i]];
        out[i + 1] = lut[data[i + 1]];
        out[i + 2] = lut[data[i + 2]];
        out[i + 3] = data[i + 3];
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').putImageData(new ImageData(out, width, height), 0, 0);
      return canvas;
    } catch {
      return null;
    }
  }

  /** Median lightness of the whole frame, for spotting a photo taken in the dark. */
  function frameBrightness(imageData) {
    const { data, width, height } = imageData;
    const step = Math.max(1, Math.round(Math.min(width, height) / 200));
    const lum = [];
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const i = (y * width + x) * 4;
        lum.push(0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]);
      }
    }
    if (!lum.length) return null;
    lum.sort((a, b) => a - b);
    const v = lum[Math.floor(lum.length / 2)];
    return rgbToLab(v, v, v).L;
  }

  /**
   * Interquartile spread of a set of lightness values.
   *
   * The quartiles rather than the full range on purpose: one patch catching a
   * nostril or an earring should not condemn a photo, but half the face sitting
   * in shadow should.
   */
  function spreadOf(values) {
    if (!values || values.length < 8) return null;
    const sorted = values.slice().sort((a, b) => a - b);
    const at = (p) => sorted[Math.floor((sorted.length - 1) * p)];
    return at(0.75) - at(0.25);
  }

  // ==========================================================================
  // Face analysis
  // ==========================================================================

  let landmarkerPromise = null;

  async function getLandmarker() {
    if (landmarkerPromise) return landmarkerPromise;

    landmarkerPromise = (async () => {
      const vision = await import(/* webpackIgnore: true */ MEDIAPIPE.module);
      const fileset = await vision.FilesetResolver.forVisionTasks(MEDIAPIPE.wasm);
      return vision.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MEDIAPIPE.model, delegate: 'GPU' },
        runningMode: 'IMAGE',
        // We look for up to four faces, not because we can read four, but
        // because we have to know when there is more than one person in the
        // shot. A group photo gets refused rather than guessed at.
        numFaces: 4,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false,
      });
    })().catch((err) => {
      landmarkerPromise = null;
      throw err;
    });

    return landmarkerPromise;
  }

  /*
    A second face has to be a real second person, not a detector artefact or a
    face on a poster in the background. We measure each detection against the
    biggest one: anything under this share of its area is ignored.
  */
  const MIN_SECONDARY_FACE_AREA = 0.08;

  function faceExtent(landmarks) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const lm of landmarks) {
      if (lm.x < minX) minX = lm.x;
      if (lm.x > maxX) maxX = lm.x;
      if (lm.y < minY) minY = lm.y;
      if (lm.y > maxY) maxY = lm.y;
    }
    const w = maxX - minX;
    const h = maxY - minY;
    return { w, h, area: w * h };
  }

  /**
   * How many people are actually in the frame, and which detection is the
   * subject. The subject is the largest face: the person who took the photo of
   * themselves is nearest the camera.
   */
  function countPeople(faces) {
    if (!faces || !faces.length) return { count: 0, primary: -1 };

    const extents = faces.map(faceExtent);
    let primary = 0;
    for (let i = 1; i < extents.length; i += 1) {
      if (extents[i].area > extents[primary].area) primary = i;
    }

    const cutoff = extents[primary].area * MIN_SECONDARY_FACE_AREA;
    let count = 0;
    for (const e of extents) {
      if (e.area >= cutoff) count += 1;
    }

    return { count, primary };
  }

  /**
   * Take an image element, find the face, sample skin, and return measured
   * colour plus a quality assessment.
   */
  async function analyseImage(imgEl) {
    const landmarker = await getLandmarker();

    // Work at a bounded size - bigger gains us nothing and costs CPU.
    const scale = Math.min(1, 900 / Math.max(imgEl.naturalWidth, imgEl.naturalHeight));
    const w = Math.round(imgEl.naturalWidth * scale);
    const h = Math.round(imgEl.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(imgEl, 0, 0, w, h);

    // Colour is always read from these pixels, exactly as the camera recorded
    // them. Nothing below is allowed to write back into this.
    const imageData = ctx.getImageData(0, 0, w, h);
    const frameL = frameBrightness(imageData);

    /*
      Finding the face and measuring the skin are two different jobs, and only
      one of them cares about exposure.

      A dim photo defeats the landmarker long before it defeats the maths: the
      detector wants edges and a dark frame has none, so it returns nothing and
      the customer gets "we could not find a face" for a photo that plainly
      contains one. So when the first pass finds nothing we hand the detector a
      brightened copy and ask again. The copy is thrown away immediately. Every
      colour sample below still comes from `imageData`, the untouched original,
      so brightening buys us the landmark positions and changes no measurement.
    */
    let result = landmarker.detect(canvas);
    let recovered = false;

    if (!result.faceLandmarks || !result.faceLandmarks.length) {
      for (const gain of DETECTION_GAINS) {
        const lifted = liftForDetection(imageData, gain);
        if (!lifted) break;
        const attempt = landmarker.detect(lifted);
        if (attempt.faceLandmarks && attempt.faceLandmarks.length) {
          result = attempt;
          recovered = true;
          break;
        }
      }
    }

    if (!result.faceLandmarks || !result.faceLandmarks.length) {
      return { ok: false, reason: 'no_face' };
    }

    // More than one person in the shot: stop here. We cannot tell whose shade
    // the customer wants, and a guess would be worse than no answer.
    const people = countPeople(result.faceLandmarks);
    if (people.count > 1) {
      return { ok: false, reason: 'multiple_faces', faceCount: people.count };
    }

    const landmarks = result.faceLandmarks[people.primary];

    /*
      How dim the frame is decides how fussy the skin test may be.

      `looksLikeSkin` exists to throw away the wall behind a turned head, and
      its floors are set for a normally exposed photo. Under a dim bulb real
      skin loses saturation and lightness together, so those same floors start
      throwing away the face as well and we end up refusing a photo we could
      have read. In a dark frame the floors come down. The ordering test
      (r > g > b) stays exactly as it was, and that is the part actually doing
      the work of telling skin from wallpaper.
    */
    const dim = frameL != null && frameL < DARK_FRAME_L;

    // Sample each region, discarding noisy patches.
    const regions = {};
    const issues = [];

    // Every accepted patch's lightness, kept so we can judge how evenly the
    // face is lit before we trust any average taken from it.
    const patchLightness = [];

    for (const [name, indices] of Object.entries(REGIONS)) {
      const patches = [];
      for (const idx of indices) {
        const lm = landmarks[idx];
        if (!lm) continue;
        const patch = samplePatch(imageData, lm.x * w, lm.y * h, PATCH_RADIUS);
        // Variance rejects hair, shadow edges, nostrils and jewellery; the skin
        // test rejects whatever sits behind a face turned away from the camera,
        // where a landmark can land on the wall rather than the cheek.
        if (patch && patch.variance < 700 && looksLikeSkin(patch, dim)) {
          patches.push(patch);
          patchLightness.push(rgbToLab(patch.r, patch.g, patch.b).L);
        }
      }

      if (!patches.length) continue;

      const r = median(patches.map((p) => p.r));
      const g = median(patches.map((p) => p.g));
      const b = median(patches.map((p) => p.b));

      regions[name] = {
        rgb: { r: Math.min(255, r), g: Math.min(255, g), b: Math.min(255, b) },
        lab: rgbToLab(Math.min(255, r), Math.min(255, g), Math.min(255, b)),
        patchCount: patches.length,
      };
    }

    /*
      Two regions normally, one when the frame is dark.

      Two agreeing regions is the sane bar: it means the cheek and the forehead
      told the same story. But a dim photo often only yields one clean region,
      and refusing it sends a customer away empty-handed over a reading we could
      have made and flagged. One region is a weaker measurement, not an absent
      one, so in a dark frame we take it and mark the result uncertain.
    */
    if (Object.keys(regions).length < (dim ? 1 : 2)) {
      return { ok: false, reason: 'insufficient_skin' };
    }

    // How much the samples disagree with each other, which is how we know
    // whether the average taken from them means very much.
    const spread = spreadOf(patchLightness);
    const unevenLight = spread != null && spread > UNCERTAIN_LIGHT_SPREAD_L;

    /*
      What we can honestly say about the light.

      Not how bright the room was, which nothing in the frame reliably tells us,
      but whether this photo is one we should be confident about. A very dark
      picture, a face with a blown-out or crushed patch on it, or light falling
      unevenly across it all make the depth reading shaky. We keep the reading,
      because it still ranks the shades sensibly, and we mark it so the swatch
      screen asks rather than assumes.
    */
    const brightestRegion = Math.max(...Object.values(regions).map((r) => r.lab.L));
    const darkestRegion = Math.min(...Object.values(regions).map((r) => r.lab.L));
    const darkFrame = frameL != null && frameL < DARK_FRAME_L;
    const clipped = brightestRegion > BLOWN_REGION_L || darkestRegion < CRUSHED_REGION_L;
    /*
      `recovered` counts as low light on its own.

      A face the detector could only find once we brightened the frame was, by
      definition, too dark to see properly, and that stays true when a bright
      window behind her keeps the frame's median above the dark threshold.
    */
    const uncertain = unevenLight || darkFrame || clipped || recovered;

    /*
      Drop any region sitting in shadow.

      A face lit from one side reads twenty L* darker on the far cheek. That
      cheek is not a different colour, it is the same skin with less light on
      it, and averaging it in only ever pulls the answer too deep. The lit
      regions are the honest ones.
    */
    const lightest = Math.max(...Object.values(regions).map((r) => labLToY(r.lab.L)));
    const shadowed = Object.keys(regions).filter(
      (n) => labLToY(regions[n].lab.L) < lightest * SHADOW_LUMINANCE_RATIO
    );
    // Never sample below two regions, however uneven the light is.
    const keepable = Object.keys(regions).filter((n) => !shadowed.includes(n));
    const names = (keepable.length >= 2
      ? keepable
      : Object.keys(regions).sort((a, b) => regions[b].lab.L - regions[a].lab.L).slice(0, 2));
    const dropped = Object.keys(regions).filter((n) => !names.includes(n));

    /*
      The jawline is the reference, not the cheeks.

      This is how foundation is matched at a counter, and for good reason.
      Cheeks flush, catch blusher and show rosacea; foreheads run oilier and
      sit closer to the light. The jaw is the calmest skin on the face and the
      closest match to the neck, which is what a foundation has to blend into.

      The earlier weighting had it backwards, cheeks highest and jaw middling.
      On the test face that cost about 3 L*: cheeks read 68 and 75, jaw 77.5.
    */
    const weights = { forehead: 0.5, leftCheek: 0.9, rightCheek: 0.9, jaw: 1.6 };
    let totalWeight = 0;
    const acc = { L: 0, a: 0, b: 0, r: 0, g: 0, b2: 0 };

    for (const name of names) {
      const w2 = weights[name] ?? 1;
      acc.L += regions[name].lab.L * w2;
      acc.a += regions[name].lab.a * w2;
      acc.b += regions[name].lab.b * w2;
      acc.r += regions[name].rgb.r * w2;
      acc.g += regions[name].rgb.g * w2;
      acc.b2 += regions[name].rgb.b * w2;
      totalWeight += w2;
    }

    const lab = { L: acc.L / totalWeight, a: acc.a / totalWeight, b: acc.b / totalWeight };
    const rgb = {
      r: Math.round(acc.r / totalWeight),
      g: Math.round(acc.g / totalWeight),
      b: Math.round(acc.b2 / totalWeight),
    };

    // ---- Quality checks --------------------------------------------------
    let quality = 1;

    if (lab.L > 88) {
      issues.push('The photo is too bright. Try moving out of direct light.');
      quality -= 0.35;
    }
    if (lab.L < 22) {
      issues.push("It is quite dark. Try facing a window.");
      quality -= 0.35;
    }

    // Left/right cheek disagreement means directional lighting.
    if (regions.leftCheek && regions.rightCheek) {
      const asymmetry = deltaE2000(regions.leftCheek.lab, regions.rightCheek.lab);
      if (asymmetry > 8) {
        issues.push('One side of your face is much brighter than the other. Try to face the light straight on.');
        quality -= 0.25;
      }
    }

    if (darkFrame) {
      issues.push('That photo was taken somewhere quite dark, which makes skin read deeper than it is.');
      quality -= 0.3;
    }
    if (clipped) {
      issues.push('Part of your face is blown out or lost in shadow, so the reading is approximate.');
      quality -= 0.2;
    }

    if (unevenLight) {
      issues.push('The light is falling unevenly across your face, so this reading is approximate.');
      quality -= 0.2;
    }
    if (dropped.length) quality -= 0.1;
    if (names.length < 3) quality -= 0.15;

    return {
      ok: true,
      lab,
      rgb,
      regions,
      frameL: frameL == null ? null : +frameL.toFixed(1),
      spread: spread == null ? null : +spread.toFixed(1),
      uncertain,
      lowLight: Boolean(darkFrame || recovered),
      recovered,
      usedRegions: names,
      shadowedRegions: dropped,
      quality: Math.max(0.1, Math.min(1, quality)),
      issues: issues.filter(Boolean),
      canvas,
      landmarks,
      dims: { w, h },
    };
  }

  /**
   * Build the tiny strip of skin crops that gets sent to the API.
   * Four 64x64 crops side by side = 256x64 = 8x2 = 16 patches of image tokens.
   * That is roughly 20 tokens. A full selfie would be 15-20x that for no
   * additional useful signal.
   */
  function buildPatchStrip(analysis) {
    const names = Object.keys(analysis.regions);
    const size = PATCH_STRIP_SIZE;
    const strip = document.createElement('canvas');
    strip.width = size * names.length;
    strip.height = size;
    const sctx = strip.getContext('2d');

    const src = analysis.canvas;
    const { w, h } = analysis.dims;

    names.forEach((name, i) => {
      const indices = REGIONS[name];
      const lm = analysis.landmarks[indices[0]];
      if (!lm) return;
      const cx = lm.x * w;
      const cy = lm.y * h;
      const crop = 40;
      sctx.drawImage(
        src,
        Math.max(0, cx - crop / 2),
        Math.max(0, cy - crop / 2),
        crop,
        crop,
        i * size,
        0,
        size,
        size
      );
    });

    return strip.toDataURL('image/jpeg', 0.9);
  }

  /** Downscaled full photo for storage only. Never sent to the model. */
  function buildStorablePhoto(imgEl) {
    const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(imgEl.naturalWidth, imgEl.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(imgEl.naturalWidth * scale);
    canvas.height = Math.round(imgEl.naturalHeight * scale);
    canvas.getContext('2d').drawImage(imgEl, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.82);
  }

  // ==========================================================================
  // Matching
  // ==========================================================================

  /** Score every shade from the quiz answers alone. Returns 0-1 per shade. */
  function scoreFromQuiz(shades, answers) {
    const depthAnswer = answers.__depth;
    const undertoneAnswer = answers.__undertone;

    const depths = shades.map((s) => s.depthIndex).filter((d) => d > 0);
    const range = Math.max(1, Math.max(...depths) - Math.min(...depths));

    return shades.map((shade) => {
      let score = 0;
      let weight = 0;

      if (depthAnswer != null && shade.depthIndex > 0) {
        const distance = Math.abs(shade.depthIndex - depthAnswer);
        score += (1 - Math.min(1, distance / range)) * 3;
        weight += 3;
      }

      if (undertoneAnswer && undertoneAnswer !== 'unknown' && shade.undertone) {
        let match;
        if (shade.undertone === undertoneAnswer) match = 1;
        else if (shade.undertone === 'neutral' || undertoneAnswer === 'neutral') match = 0.65;
        else match = 0.2; // warm vs cool - a real mismatch
        score += match * 1.5;
        weight += 1.5;
      }

      return weight ? score / weight : 0.5;
    });
  }

  /**
   * Distance between measured skin and a foundation shade.
   *
   * CIEDE2000 is the right tool for asking "do these two paint chips match".
   * It is the wrong tool, used raw, for asking "which foundation suits this
   * face", because it weights chroma and hue about as heavily as lightness.
   * Skin carries redness that foundation is meant to COVER, not match: flush,
   * rosacea, irritation, windburn, a hot room. The test face that prompted this
   * measures a* 22.8 against a shade range that runs a* 9.5 to 13.5. Judged on
   * raw colour difference she is far from every shade we sell, and the ranking
   * starts being decided by how red she happens to be today.
   *
   * So the comparison is split. Depth is judged on lightness alone, which is
   * stable and is what picking the wrong shade actually shows up as. Undertone
   * is judged on hue angle, which survives a flush far better than a* does,
   * and counts for much less. Raw ΔE2000 is still computed and still shown to
   * the customer, because it is the honest measure of "how close is this
   * colour", and it is what a real measured Lab value would improve.
   */
  function skinDistance(measured, shade) {
    const dL = Math.abs(measured.L - shade.L);

    const hue = (lab) => {
      let h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
      return h < 0 ? h + 360 : h;
    };
    let dH = Math.abs(hue(measured) - hue(shade));
    if (dH > 180) dH = 360 - dH;

    // 1 L* of depth error is worth about 4 degrees of hue error. A shade one
    // step too deep is visible from across a room; a shade a touch too warm is
    // not.
    return dL + dH / 4;
  }

  /** Score every shade from the measured Lab value. Returns { score, deltaE }. */
  function scoreFromPhoto(shades, measuredLab) {
    return shades.map((shade) => {
      const lab = normaliseLab(shade);
      if (!lab) return { score: null, deltaE: null };

      const dist = skinDistance(measuredLab, lab);
      // A whole shade step is about 6 to 7 L*, so 14 covers roughly two steps.
      const score = Math.max(0, 1 - dist / 14);

      // Reported to the customer and used for the confidence bar. This is the
      // true colour difference, not the depth-weighted one used for ranking.
      return { score, deltaE: deltaE2000(measuredLab, lab) };
    });
  }

  /*
    What a real face measures, at each rung of the depth question.

    Six numbers, one per tile, each the median skin L* of the photograph on that
    tile - measured through this file's own REGIONS / samplePatch / looksLikeSkin
    code, on forehead and jaw only, because blush and bronzer live on the cheeks.

    Everything about the photo route's depth reading rests on these. They are
    the bridge between "what the camera saw" and "which rung of the ladder",
    and before they existed the code crossed that gap by comparing skin against
    foundation values and hoping. It did not work: see the note in
    deriveFromLab.

    If the six tile photographs are ever changed, THESE MUST BE REMEASURED. A
    tile and its anchor are two halves of one statement; leave one behind and
    the photo route starts quietly disagreeing with the swatch a customer would
    have picked herself. docs/SKIN-LOCUS.md has the method.
  */
  const PHOTO_DEPTH_ANCHORS = [
    { depth: 1, L: 69.8 },
    { depth: 2, L: 65.4 },
    { depth: 3, L: 61.5 },
    { depth: 4, L: 54.7 },
    { depth: 5, L: 47.7 },
    { depth: 6, L: 40.2 },
  ];

  /**
   * Which rung of the ladder a measured lightness sits on.
   *
   * Nearest anchor, and deliberately nothing cleverer. Interpolating would
   * invent depth steps the question does not offer, and the customer can only
   * ever be handed one of six answers.
   *
   * Note there is no clamping quietly going on at the deep end: a face darker
   * than the deepest anchor lands on the deepest rung, which is the one that
   * says "deeper than all of these" and is honest about being past the range.
   */
  function depthForMeasuredL(L) {
    if (typeof L !== 'number' || !isFinite(L)) return null;
    let best = null;
    let bestD = Infinity;
    for (const a of PHOTO_DEPTH_ANCHORS) {
      const d = Math.abs(L - a.L);
      if (d < bestD) { bestD = d; best = a.depth; }
    }
    return best;
  }

  /**
   * Read the quiz answers straight off the photo.
   *
   * Depth comes from whichever shade the measured skin is closest to - the
   * same CIEDE2000 comparison the matcher itself uses, so the two can never
   * disagree.
   *
   * Undertone comes from the hue angle across the a and b axes. Skin sits
   * roughly 40-70 degrees: swing towards yellow and it reads warm, towards
   * red/pink and it reads cool. It's a coarse call, which is why the result
   * screen shows what we read and lets the customer overrule it.
   */
  function deriveFromLab(lab, shades) {
    /*
      Depth comes from the calibration ladder, not from the shade table.

      This is the fix for the oldest bug in this project: deep-skinned customers
      were handed Medium and Medium-Tan. The cause was a units error dressed up
      as a colour comparison. The shade table's L* values describe FOUNDATION -
      liquid in a bottle, measured or estimated as pigment - and this function
      was comparing PHOTOGRAPHED SKIN against them directly. The two do not live
      on the same scale, so a customer could be nearer to the wrong shade in
      arithmetic while being obviously the other one to the eye. The store's own
      deeper-skinned model measures L* 51.6 against a table whose deepest value
      is 45: she falls off the end of a ruler that was never marked for her.

      PHOTO_DEPTH_ANCHORS is that missing ruler. Each entry is a depth step
      paired with the L* that a real face at that depth actually measures
      through this very pipeline - same regions, same patches, same skin test -
      so a customer's photo is now compared against photographs of skin rather
      than against paint.

      The anchors are the six tiles on the depth question. That is deliberate
      and it is the point: the rung the photo picks and the rung she would pick
      by eye are now the same rung, measured the same way.
    */
    const depthFromPhoto = depthForMeasuredL(lab.L);

    // The shade named on screen follows the depth we just derived, so the
    // readout can never say "Medium" while the match says something else.
    let nearest =
      shades.find((sh) => sh.depthIndex === depthFromPhoto) || null;

    if (!nearest) {
      // No shade at that step - a family with gaps, or a depth past the end of
      // the range. Fall back to the closest shade by lightness alone.
      let best = Infinity;
      for (const shade of shades) {
        const shadeLab = normaliseLab(shade);
        if (!shadeLab) continue;
        const d = Math.abs(lab.L - shadeLab.L);
        if (d < best) { best = d; nearest = shade; }
      }
    }

    let hue = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
    if (hue < 0) hue += 360;

    // Bands calibrated against measured skin rather than the abstract colour
    // wheel: real skin clusters between about 40 and 75 degrees, so splitting
    // at 50/60 puts roughly a third of the population in each bucket. Wider
    // bands (say warm at 55+) collapse almost everyone into "warm".
    const undertone = hue >= 60 ? 'warm' : hue < 50 ? 'cool' : 'neutral';

    return {
      depth: depthFromPhoto,
      undertone,
      nearest,
      hue,
    };
  }

  /** Approximate hex for the measured skin colour, for the readout chip. */
  function rgbToHex({ r, g, b }) {
    return '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
  }

  /*
    The page the customer came from, when the link says so.

    ?return_to= is what the "Find Your Shade" button already on La Mior's
    product pages sends. It is a value anyone can put in a link, so it is only
    ever accepted as a path on this shop: "/products/x" yes; "//other.site",
    "https://other.site" and "/\other.site" (which browsers read as
    "//other.site") are all refused by resolving it and checking the origin.
  */
  function safeReturnPath() {
    let raw;
    try {
      raw = new URLSearchParams(window.location.search).get('return_to');
    } catch {
      return null;
    }
    if (!raw) return null;
    // URL parsers silently drop tabs and newlines, so "/\t/other.site" would
    // become "//other.site" after any check made on the raw string. Strip them
    // first, then require exactly one leading slash - not "//", not "/\".
    raw = raw.replace(/[\t\n\r]/g, '');
    if (!/^\/(?![\/\\])/.test(raw)) return null;
    try {
      const u = new URL(raw, window.location.href);
      if (u.origin !== window.location.origin || u.host !== window.location.host) return null;
      return u.pathname + u.search;
    } catch {
      return null;
    }
  }

  /*
    Which product the finder should narrow to.

    Two spellings reach this page. The snippet shipped with the finder sends
    ?product=<handle>. The button already on the product pages sends
    ?return_to=/products/<handle> - built for the way back rather than for
    narrowing, but it names the product just as plainly. v28 only listened for
    the first, so arriving from Daily Sunbath's own button matched across every
    product and could hand her Glow Burst. Both are read now; ?product= wins
    if a link somehow carries the two.
  */
  function productFromUrl() {
    try {
      const direct = new URLSearchParams(window.location.search).get('product');
      if (direct) return direct;
    } catch {
      return null;
    }
    const back = safeReturnPath();
    const m = back && /\/products\/([^/?#]+)/.exec(back);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }

  /**
   * Which product a shade belongs to, by handle. Metaobject shades rendered by
   * an older section carry only a product URL, so fall back to reading the
   * handle out of it.
   */
  function productKeyOf(shade) {
    if (shade.productHandle) return shade.productHandle;
    const m = /\/products\/([^/?#]+)/.exec(shade.productUrl || '');
    return m ? m[1] : null;
  }

  /** Accept either a stored Lab object or fall back to converting the hex. */
  function normaliseLab(shade) {
    if (shade.lab && typeof shade.lab.L === 'number') return shade.lab;
    const rgb = hexToRgb(shade.swatchHex);
    return rgb ? rgbToLab(rgb.r, rgb.g, rgb.b) : null;
  }


  /**
   * Fuse quiz and photo signals into a ranked result.
   *
   * The photo carries more weight when its quality is high, but never all of
   * it - a customer who says "I want sheer coverage and I'm cool-toned" has
   * told us something the pixels cannot.
   */
  function match(shades, answers, analysis) {
    if (!shades.length) return null;

    const quizScores = scoreFromQuiz(shades, answers);
    const photoScores = analysis?.ok
      ? scoreFromPhoto(shades, analysis.lab)
      : shades.map(() => ({ score: null, deltaE: null }));

    const photoWeight = analysis?.ok ? 0.35 + 0.3 * analysis.quality : 0;
    const quizWeight = 1 - photoWeight;

    const ranked = shades
      .map((shade, i) => {
        const q = quizScores[i];
        const p = photoScores[i].score;
        const combined = p == null ? q : q * quizWeight + p * photoWeight;
        return {
          shade,
          score: combined,
          quizScore: q,
          photoScore: p,
          deltaE: photoScores[i].deltaE,
        };
      })
      .sort((a, b) => b.score - a.score);

    const top = ranked[0];
    const runnerUp = ranked[1];

    // Confidence blends "how far ahead is the winner" with photo quality.
    const separation = runnerUp ? Math.min(1, (top.score - runnerUp.score) * 4) : 0.7;
    let confidence = 0.45 + separation * 0.4;
    if (analysis?.ok) confidence = confidence * (0.65 + 0.35 * analysis.quality);
    else confidence *= 0.85; // quiz-only is inherently less certain
    confidence = Math.max(0.2, Math.min(0.95, confidence));

    // Out of range detection: either the customer told us, or their measured
    // lightness sits well below our deepest shade.
    let outOfRange = Boolean(answers.__outOfRange);
    if (analysis?.ok && analysis.quality > 0.5) {
      const deepest = shades
        .map((s) => normaliseLab(s))
        .filter(Boolean)
        .reduce((min, lab) => (lab.L < min ? lab.L : min), Infinity);
      if (Number.isFinite(deepest) && analysis.lab.L < deepest - 9) outOfRange = true;
    }

    /*
      Past the end of the range, but not far enough to refuse.

      The confidence above measures how far the winner is ahead of the
      runner-up, and at the end of a range that goes wrong in the flattering
      direction. Scoped to a product that stops at Tan, a face one rung deeper
      gets Tan with a wide lead over Medium-Tan, and "wide lead" reads as
      "confident". It is the least wrong answer, not a right one.

      Judged on depth rungs, not on L*, because depth is what the photo route
      measures against real skin (see deriveFromLab); comparing skin L* with
      foundation L* here would reintroduce the units error that function exists
      to remove.
    */
    let edge = null;
    const rungs = shades.map((s) => s.depthIndex).filter((d) => d > 0);
    if (!outOfRange && answers.__depth != null && rungs.length) {
      if (answers.__depth > Math.max(...rungs)) edge = 'deeper';
      else if (answers.__depth < Math.min(...rungs)) edge = 'lighter';
    }
    if (edge) confidence = Math.min(confidence, 0.6);

    return { ranked, top, runnerUp, confidence, outOfRange, edge, photoWeight };
  }

  // ==========================================================================
  // API client
  // ==========================================================================

  async function callApi(endpoint, payload, timeoutMs = 12000) {
    if (!endpoint) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(endpoint.replace(/\/$/, '') + '/analyse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      // Deliberately silent. The deterministic match already succeeded; the
      // API is an enhancement and must never block the result screen.
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  // ==========================================================================
  // DOM helpers
  // ==========================================================================

  /*
    Line icons, drawn inline rather than pulled from a font or a CDN.

    These replace the emoji the first version used. Emoji render differently on
    every device, sit at a different weight to the text around them, and on a
    beauty site they read as a placeholder someone forgot to swap out. These are
    1.5px strokes on a 24 grid, which matches the weight of Poppins Medium.
  */
  const ICONS = {
    camera:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="22" height="22">' +
      '<path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.3 4.7h5.4a1 1 0 0 1 .83.45l.94 1.4a1 1 0 0 0 .83.45h2.2A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z"/>' +
      '<circle cx="12" cy="13" r="3.4"/></svg>',
    list:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="22" height="22">' +
      '<path d="M9 6h11M9 12h11M9 18h11"/><path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01"/></svg>',
    lock:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">' +
      '<rect x="4.5" y="10" width="15" height="10" rx="2.5"/><path d="M8 10V7.5a4 4 0 0 1 8 0V10"/></svg>',
    clock:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">' +
      '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 1.8"/></svg>',
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" width="12" height="12">' +
      '<path d="M4.5 12.5 9.5 17.5 19.5 6.5"/></svg>',
    sparkle:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="20" height="20">' +
      '<path d="M12 3.5 13.9 9 19.5 11 13.9 13 12 18.5 10.1 13 4.5 11 10.1 9z"/><path d="M18.5 4v3M20 5.5h-3"/></svg>',
    alert:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">' +
      '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.8v4.6M12 16.1h.01"/></svg>',
    bulb:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">' +
      '<path d="M9.5 17.5h5M10.2 20.3h3.6"/><path d="M12 3.5a5.6 5.6 0 0 0-3.3 10.1c.6.45.95 1.1.95 1.8v.1h4.7v-.1c0-.7.35-1.35.95-1.8A5.6 5.6 0 0 0 12 3.5z"/></svg>',
    // Icons for the quieter controls. A back arrow and a redo arrow are read
    // faster than the words beside them, which is the point of putting them on
    // buttons people use when they are already unsure.
    back:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">' +
      '<path d="M14.5 5.5 8 12l6.5 6.5"/></svg>',
    redo:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">' +
      '<path d="M20 11.5a8 8 0 1 1-2.6-5.9"/><path d="M20.5 4v4.5H16"/></svg>',
    image:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">' +
      '<rect x="3.5" y="5" width="17" height="14" rx="2.5"/><circle cx="9" cy="10" r="1.6"/>' +
      '<path d="M4 16.5 9 12l3.5 3 2.5-2 4.5 4"/></svg>',
    cross:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" width="12" height="12">' +
      '<path d="M6 6l12 12M18 6 6 18"/></svg>',
    plus:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">' +
      '<path d="M12 5.5v13M5.5 12h13"/></svg>',
    minus:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">' +
      '<path d="M5.5 12h13"/></svg>',
  };

  /*
    Three small drawings of what a usable photo looks like.

    Line art rather than a photograph, because a drawing cannot look like one
    particular woman and so cannot tell anyone else this is not for them. If the
    shop uploads a real example photo in the theme editor it takes this place,
    which is better still: nothing explains a good photo faster than seeing one.
  */
  const PHOTO_GUIDE = [
    {
      ok: true,
      label: 'Face a window',
      art:
        '<svg viewBox="0 0 96 72" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<rect x="6" y="12" width="26" height="40" rx="2"/><path d="M19 12v40M6 32h26"/>' +
        '<path d="M36 24h8M36 32h8M36 40h8" opacity="0.6"/>' +
        '<circle cx="70" cy="28" r="12"/><path d="M55 60c3-9 8-13 15-13s12 4 15 13"/></svg>',
    },
    {
      ok: false,
      label: 'Not with the light behind you',
      art:
        '<svg viewBox="0 0 96 72" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<circle cx="34" cy="28" r="12"/><path d="M19 60c3-9 8-13 15-13s12 4 15 13"/>' +
        '<rect x="62" y="12" width="26" height="40" rx="2"/><path d="M75 12v40M62 32h26"/>' +
        '<path d="M58 24h-8M58 32h-8M58 40h-8" opacity="0.6"/></svg>',
    },
    {
      ok: false,
      label: 'Nothing covering your face',
      art:
        '<svg viewBox="0 0 96 72" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<circle cx="48" cy="28" r="12"/><path d="M33 60c3-9 8-13 15-13s12 4 15 13"/>' +
        // A hand resting on the jaw, which is what covered the sample points
        // on the photo that started all this.
        '<path d="M60 30c5-1 8 1 9 5s-1 9-5 12l-6 4"/><path d="M62 36h6M62 41h5"/>' +
        // And a fringe over the forehead on the other side.
        '<path d="M36 20c-3 4-4 8-3 12"/></svg>',
    },
  ];

  /**
   * A button with an icon in front of its label.
   *
   * Everything that is not the main action on a screen goes through here, so
   * the quiet controls still look like controls. They used to be underlined
   * text, which on a phone is indistinguishable from a caption.
   */
  function iconButton(label, icon, attrs = {}) {
    const { class: cls = 'sf-btn sf-btn--ghost', ...rest } = attrs;
    return el('button', { type: 'button', class: cls, ...rest }, [
      icon ? el('span', { class: 'sf-btn__icon', 'aria-hidden': 'true', html: icon }) : null,
      el('span', { text: label }),
    ]);
  }

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k === 'class') node.className = v;
      else if (k === 'html') node.innerHTML = v;
      else if (k === 'text') node.textContent = v;
      else if (k.startsWith('on') && typeof v === 'function') {
        node.addEventListener(k.slice(2).toLowerCase(), v);
      } else if (k === 'style' && typeof v === 'object') {
        Object.assign(node.style, v);
      } else node.setAttribute(k, v === true ? '' : String(v));
    }
    for (const child of [].concat(children)) {
      if (child == null) continue;
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
  }

  /*
    Shopify's money_format usually carries markup, for example
    "<span class=money>Rs. {{amount}}</span>". We render prices as plain text,
    so the tags have to come out or the customer sees the raw span.
  */
  function formatMoney(cents, format) {
    if (cents == null) return '';
    const amount = (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    /*
      The store's money format is "Rs. {{amount}}". The rupee sign is what the
      customer expects to see next to a price, so it is substituted here rather
      than in Shopify's settings, which would change every price on every page
      and is the shop's call to make, not this widget's.
    */
    const plain = String(format || '')
      .replace(/<[^>]*>/g, '')
      .replace(/\bRs\.?(?=\s|$|\{)/gi, '₹')
      .replace(/\bINR\b/gi, '₹')
      .trim();
    if (plain.includes('{{')) {
      return plain
        .replace(/\{\{\s*\w+\s*\}\}/, amount)
        .replace(/₹\s+/, '₹')
        .replace(/\s+/g, ' ')
        .trim();
    }
    return '₹' + amount;
  }

  // ==========================================================================
  // Past results
  // --------------------------------------------------------------------------
  // Kept in this browser only. It never reaches a server, so it survives a
  // refresh or a visit next week but not a different device. That is the right
  // trade for something this small: no account needed, nothing to leak.
  // ==========================================================================

  /*
    Past matches are kept per product family.

    One store can run several finders: foundation on one page, concealer on
    another, lip on a third. They share a browser, so a single history key would
    show a foundation result on the lip page, which is worse than showing
    nothing. The key is scoped by family, and the old unscoped key is migrated
    into the foundation bucket the first time a shopper returns.
  */
  const HISTORY_KEY_BASE = 'lamior:shade-history';
  const historyKey = (family) => `${HISTORY_KEY_BASE}:${family || 'all'}`;
  const HISTORY_MAX = 6;

  function readHistory(family) {
    try {
      migrateLegacyHistory(family);
      const raw = localStorage.getItem(historyKey(family));
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  function writeHistory(entry, family) {
    try {
      /*
        One visit leaves one entry.

        Correcting the depth re-runs the match, and without the session filter
        every tap of "I'm lighter" left its own line in past matches: Tan, then
        Medium-Tan, then Medium, as though she had been three people. The last
        answer of a session replaces the earlier ones.
      */
      const list = readHistory(family).filter(
        (e) => e.handle !== entry.handle && !(entry.sessionId && e.sessionId === entry.sessionId)
      );
      list.unshift(entry);
      localStorage.setItem(historyKey(family), JSON.stringify(list.slice(0, HISTORY_MAX)));
    } catch {
      /* private browsing, storage disabled, quota. Not worth breaking over. */
    }
  }

  function clearHistory(family) {
    try { localStorage.removeItem(historyKey(family)); } catch { /* ignore */ }
  }

  /*
    Anyone who used the finder before it was split by family has results under
    the old unscoped key. Move them once into whichever family they are being
    asked for, rather than silently losing them.
  */
  function migrateLegacyHistory(family) {
    try {
      const legacy = localStorage.getItem(HISTORY_KEY_BASE);
      if (!legacy) return;
      const target = historyKey(family);
      if (!localStorage.getItem(target)) localStorage.setItem(target, legacy);
      localStorage.removeItem(HISTORY_KEY_BASE);
    } catch {
      /* ignore */
    }
  }

  function timeAgo(ts) {
    const mins = Math.round((Date.now() - ts) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + (mins === 1 ? ' minute ago' : ' minutes ago');
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return hrs + (hrs === 1 ? ' hour ago' : ' hours ago');
    const days = Math.round(hrs / 24);
    if (days < 30) return days + (days === 1 ? ' day ago' : ' days ago');
    return new Date(ts).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }

  function uid() {
    if (crypto?.randomUUID) return crypto.randomUUID();
    return 'sf-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  // ==========================================================================
  // The widget
  // ==========================================================================

  class ShadeFinder {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-sf-stage]');
      this.progress = root.querySelector('[data-sf-progress]');
      this.progressBar = root.querySelector('[data-sf-progress-bar]');
      this.progressLabel = root.querySelector('[data-sf-progress-label]');
      this.endpoint = root.dataset.endpoint || '';

      const configEl = root.querySelector('[data-sf-config]');
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.error('[ShadeFinder] Bad config JSON:', err);
        return;
      }

      this.settings = this.config.settings || {};
      this.family = this.settings.family || 'all';

      /*
        Add ?sfdebug=1 to the page URL to see the numbers behind a reading.

        This is how the shade values get calibrated: photograph people whose
        correct shade you already know, read the measured L* off this panel, and
        compare it with the L* stored against the shade they actually wear. It
        is invisible to customers, who have no reason to add the parameter.
      */
      try {
        this.debug = new URLSearchParams(location.search).has('sfdebug');
      } catch {
        this.debug = false;
      }

      /*
        Questions can be scoped to a product family.

        A question with no family set shows on every finder, which keeps the
        common ones ("what colour are your veins") in one place. A question with
        a family set shows only there, so the lip finder can ask about finish
        and the foundation finder about coverage without either page carrying
        the other's questions.
      */
      /*
        Keep the walk short.

        Only two answers change which shade comes out: how deep the skin is and
        which way it leans. Coverage and concerns are useful to the shop and
        cost the customer a step each, so by default they are not asked. The
        theme editor can turn them back on.
      */
      const changesTheAnswer = (q) =>
        (q.options || []).some(
          (o) => o.scores && (typeof o.scores.depth === 'number' || typeof o.scores.undertone === 'string')
        );

      this.minimalQuiz = this.settings.minimalQuiz !== false;

      this.questions = (this.config.questions || [])
        .filter((q) => !this.minimalQuiz || changesTheAnswer(q))
        .filter((q) => !q.family || q.family === 'all' || this.family === 'all' || q.family === this.family)
        .slice()
        .sort((a, b) => (a.position || 99) - (b.position || 99))
        .map((q) => ({
          ...q,
          options: (q.options || []).slice().sort((a, b) => (a.position || 99) - (b.position || 99)),
        }));

      this.shades = this.config.shades || [];

      // Fall back to the built-in content when no metaobjects exist yet, so the
      // page is usable the moment the theme files land. Metaobjects win as soon
      // as they're created.
      if (!this.questions.length) {
        // A copy, not the constant: see the note on rebuilding below.
        this.questions = DEFAULT_QUESTIONS.map((q) => ({ ...q, options: (q.options || []).slice() }));
        this.usingDefaults = true;
      }
      if (!this.shades.length) {
        this.shades = DEFAULT_SHADES;
        this.usingDefaults = true;
      } else if (this.settings.fillBuiltInShades !== false) {
        /*
          Metaobjects win per product, not all-or-nothing.

          The shop already has shade metaobjects for Glow Burst and the
          concealer. Under the old rule - any metaobject at all switches the
          built-in table off - adding Daily Sunbath to the built-in table did
          nothing on this shop: its 18 shades were silently dropped because
          other products had entries. So each built-in product fills in only
          where the shop has no entries of its own for that product. Create a
          metaobject for a product and the built-in entries for it step aside.

          Family still applies: a concealer finder is not handed foundations.
          And it can be switched off in the theme editor ("Fill in products
          that have no shade entries"), for a shop that wants only its own.
        */
        const own = this.shades;
        const covered = new Set(own.map(productKeyOf).filter(Boolean));
        const fill = DEFAULT_SHADES.filter((sh) =>
          (this.family === 'all' || sh.family === this.family) &&
          !covered.has(productKeyOf(sh))
        );
        if (fill.length) {
          this.shades = own.concat(fill);
          this.filledFromDefaults = [...new Set(fill.map(productKeyOf))];
        }
      }
      /*
        Arriving from one product's "Find your shade" button.

        The finder is a page of its own, so without this a customer looking at
        one foundation taps "find your shade", and is handed the whole range -
        including shades of products she was not looking at. The button carries
        ?product=<handle>, and the finder narrows to that product.

        Deliberately a narrowing, not a hard filter: if the handle matches
        nothing - a renamed product, a stale link in an email, a typo - we keep
        the full range rather than showing her an empty finder. Being shown too
        much is a worse day than being shown nothing, but only slightly; being
        shown nothing is a dead end.

        This runs AFTER the fallback to the built-in shades, and has to. It
        used to run before it, which meant a shop with no shade metaobjects -
        every shop on the built-in table - filtered an empty list, found
        nothing, then loaded all 23 shades unscoped. The button
        carried the handle; the finder ignored it. Same ordering mistake as
        the photo pickers, same fix.
      */
      this.scopedProduct = null;
      // The whole table, kept after narrowing, so a customer this product
      // cannot serve can be pointed at the one that can.
      this.allShades = this.shades;
      try {
        const wanted = productFromUrl();
        if (wanted) {
          const hit = this.shades.filter(
            (sh) => sh.productHandle === wanted ||
              (sh.productUrl || '').split('?')[0].endsWith('/products/' + wanted)
          );
          if (hit.length) {
            this.shades = hit;
            this.scopedProduct = hit[0].productTitle || null;
          }
        }
      } catch { }

      /*
        The six depth photographs, applied to whichever ladder we ended up with.

        This runs AFTER the fallback above, and that ordering is the whole
        point. The section used to inject these into the metaobject question
        options, which meant a shop running on the built-in questions could set
        all six pickers in the theme editor and see absolutely nothing change -
        the questions it rendered had never been through that code. The
        photographs describe the depth ladder, not one source of questions, so
        they are applied here to whatever ladder is actually about to be drawn.

        An image already on an option wins: that is a per-answer override set in
        the metaobject, which is more specific than a section-wide setting.
      */
      const depthPhotos = this.settings.depthPhotos || [];
      const depthFocus = this.settings.depthPhotoFocus || [];

      /*
        Photographs are required for the shades the shop SELLS, not for all six.

        The last option is not a sixth shade. It is "deeper than all of these" -
        the honest admission that the range stops before she does - and asking a
        shop to photograph a woman for a foundation it does not make was a
        design error on my part.

        What the old all-six rule was really protecting is still protected:
        within the range, it is all faces or all colour, never faces at the light
        end and a dot at the deep end. The out-of-range option sits outside that
        set and gets its own treatment below, which is the truthful thing to do
        anyway - it is a different KIND of answer, and dressing it as a swatch
        implied a product that does not exist.
      */
      const inRangeSlots = DEFAULT_QUESTIONS.length ? 5 : 5;
      const haveInRange = depthPhotos.slice(0, inRangeSlots).filter(Boolean).length;
      if (haveInRange === inRangeSlots) {
        /*
          Rebuilt, never mutated in place.

          this.questions can BE the DEFAULT_QUESTIONS array - the fallback above
          assigns it by reference - so writing q.options here edited the module
          constant itself. Every finder mounted after the first one on that page
          then inherited the photographs whether its own section set them or
          not, which is a leak between instances and survives until reload.
        */
        this.questions = this.questions.map((q) => {
          const isLadder = (q.options || []).every(
            (o) => o.scores && typeof o.scores.depth === 'number'
          );
          if (!isLadder) return q;
          return {
            ...q,
            options: q.options.map((o) => {
              const slot = o.scores.depth - 1;
              if (o.image || slot < 0 || slot > 5) return o;
              // The out-of-range answer takes a photograph only if one was set
              // for it; it never requires one.
              const url = depthPhotos[slot];
              if (!url) return o;
              return { ...o, image: url, imageFocus: depthFocus[slot] || '50% 28%' };
            }),
          };
        });
      }

      /*
        The undertone question's colours and its optional fourth answer.

        Same shape as the depth photographs above and for the same reason: these
        are applied to whichever question is about to be drawn, so they work
        whether the quiz comes from metaobjects or from the built-in content.

        The theme editor wins here, deliberately. A colour picker always has a
        value, so there is no way to tell "left at the default" from "chosen",
        and a setting that silently loses to a metaobject would be worse than
        one that plainly governs. One place to change these: the theme editor.
      */
      /*
        "Deeper than all of these" is only true for some products.

        Glow Burst stops at Tan, so its sixth answer is genuinely off the end of
        the range and says so. Daily Sunbath has a real Deep shade at that same
        rung. Telling a customer she is past the range while a shade exists for
        her is both wrong and the exact thing this finder is supposed to stop
        doing, so when the range in play reaches depth 6 the answer becomes an
        ordinary shade: named after it, and no longer flagged out of range.
      */
      /*
        Only a family that is actually on the six-rung ladder counts. The
        concealer numbers its shades 1 to 8 on a scale of its own, so its
        "depth 6" is a mid-tone; on a finder showing every family, reading it
        as the deepest rung named the sixth answer "Croissant".
      */
      const familyMax = {};
      this.shades.forEach((sh) => {
        const f = sh.family || '';
        familyMax[f] = Math.max(familyMax[f] || 0, sh.depthIndex || 0);
      });
      const deepest = this.shades.filter(
        (sh) => sh.depthIndex === 6 && familyMax[sh.family || ''] === 6
      );
      if (deepest.length) {
        const deepName = (deepest[0].name || '').split(' ')[0] || 'Deep';
        this.questions = this.questions.map((q) => {
          const isLadder = (q.options || []).every(
            (o) => o.scores && typeof o.scores.depth === 'number'
          );
          if (!isLadder) return q;
          return {
            ...q,
            options: q.options.map((o) => {
              if (!o.scores.out_of_range) return o;
              const scores = { ...o.scores };
              delete scores.out_of_range;
              return { ...o, label: deepName, helper: 'Deepest in this range.', scores };
            }),
          };
        });
      }

      const toneColours = {
        cool: this.settings.undertoneCoolColor,
        warm: this.settings.undertoneWarmColor,
        neutral: this.settings.undertoneNeutralColor,
        unknown: this.settings.undertoneUnsureColor,
      };
      const showUnsure = this.settings.showUnsureOption === true;

      this.questions = this.questions.map((q) => {
        const isTone = (q.options || []).some((o) => o.scores && o.scores.undertone);
        if (!isTone) return q;
        return {
          ...q,
          options: q.options
            .filter((o) => showUnsure || o.scores.undertone !== 'unknown')
            .map((o) => {
              const c = toneColours[o.scores.undertone];
              return c ? { ...o, swatchHex: c } : o;
            }),
        };
      });

      if (this.filledFromDefaults && this.filledFromDefaults.length) {
        console.info(
          '[ShadeFinder] Built-in shades used for: ' + this.filledFromDefaults.join(', ') +
            '. Create shade metaobjects for a product to replace its built-in entries.'
        );
      }
      if (this.usingDefaults) {
        console.info(
          '[ShadeFinder] Running on built-in content. Create the metaobjects in ' +
            'Shopify Admin > Content > Metaobjects to edit questions and shades without a developer.'
        );
      }

      this.sessionId = uid();
      this.answers = {};
      this.step = 0;
      this.analysis = null;
      this.photoDataUrl = null;
      this.consent = false;
      this.startedAt = Date.now();

      // null until the customer picks a route; skip the chooser entirely when
      // photo matching is switched off.
      this.mode = this.settings.photoEnabled ? null : 'quiz';

      this.render();
    }

    // ---- Navigation ------------------------------------------------------

    /*
      Each route ends where the customer expects it to.

      The quiz route used to walk into a photo step at the end, which flatly
      contradicted the card they had just tapped ("no camera needed"). The photo
      is now offered on the result screen instead, as an upgrade to a match they
      already have rather than a toll gate in front of one.
    */
    get totalSteps() {
      if (this.mode === 'photo') return 1;
      return this.questions.length;
    }

    get isPhotoStep() {
      return this.mode === 'photo';
    }

    /** The question that carries the depth ladder, whatever it has been renamed to. */
    depthQuestion() {
      return this.questions.find((q) =>
        (q.options || []).some((o) => o.scores && typeof o.scores.depth === 'number')
      );
    }

    next() {
      this.step++;
      if (this.step >= this.totalSteps) this.finish();
      else this.render();
    }

    back() {
      // From the first real step, "back" returns to the route chooser rather
      // than dead-ending.
      if (this.mode === 'photo' || this.step === 0) {
        if (this.settings.photoEnabled) {
          this.mode = null;
          this.step = 0;
          this.render();
        }
        return;
      }
      this.step--;
      this.render();
    }

    updateProgress() {
      if (!this.progress) return;
      this.progress.hidden = false;
      const pct = Math.round((this.step / this.totalSteps) * 100);
      this.progressBar.style.width = pct + '%';
      this.progressLabel.textContent = `Step ${Math.min(this.step + 1, this.totalSteps)} of ${this.totalSteps}`;
    }

    render() {
      this.stage.innerHTML = '';

      if (this.mode === null) {
        this.progress.hidden = true;
        this.stage.appendChild(this.renderModeChooser());
      } else {
        this.updateProgress();
        if (this.isPhotoStep) this.stage.appendChild(this.renderPhotoStep());
        else this.stage.appendChild(this.renderQuestion(this.questions[this.step]));
      }

      /*
        preventScroll matters here. Moving focus to the new heading is what makes
        a screen reader announce the step, but the browser's default is to scroll
        that element into view, and on mount that dragged the page down to the
        widget before the customer had asked for anything. Focus for the
        announcement, scroll on our own terms below.
      */
      const heading = this.stage.querySelector('h2, h3, [tabindex]');
      if (heading && heading.focus) heading.focus({ preventScroll: true });
      this.scrollToTop();
    }

    /**
     * Put the top of the finder back in view on every screen change.
     *
     * Each screen replaces the last one in place, so after a long result page
     * or a tall grid of swatches the next screen opens with its heading
     * somewhere above the fold and the customer is looking at the middle of it.
     * Not on the very first paint, though: the widget sits partway down a page
     * and yanking someone to it before they have asked for anything is rude.
     */
    scrollToTop() {
      if (!this.painted) {
        this.painted = true;
        return;
      }
      try {
        const top = this.root.getBoundingClientRect().top + window.scrollY - SCROLL_HEADROOM;
        // Already within a few pixels of the right place: leave it alone rather
        // than firing a smooth scroll that visibly does nothing.
        if (Math.abs(top - window.scrollY) < 8) return;
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });
      } catch {
        /* Older browsers, or a detached node during teardown. Not worth breaking over. */
      }
    }

    // ---- Route chooser ---------------------------------------------------
    renderModeChooser() {
      const card = (icon, tag, title, body, meta, onClick, photo) =>
        el('button', { type: 'button', class: 'sf-mode', 'data-photo': String(Boolean(photo)), onClick }, [
          // A real photograph when the shop has uploaded one. A woman's face
          // explains this route in a way a camera icon never will.
          photo
            ? el('span', {
                class: 'sf-mode__photo',
                'aria-hidden': 'true',
                style: {
                  backgroundImage: `url(${photo})`,
                  backgroundPosition: this.settings.photoExampleFocus || '50% 30%',
                },
              })
            : el('span', { class: 'sf-mode__icon', 'aria-hidden': 'true', html: icon }),
          tag ? el('span', { class: 'sf-mode__tag', text: tag }) : null,
          el('span', { class: 'sf-mode__title', text: title }),
          el('span', { class: 'sf-mode__body', text: body }),
          el('span', { class: 'sf-mode__meta' }, [
            el('span', { 'aria-hidden': 'true', html: ICONS.clock }),
            el('span', { text: meta }),
          ]),
        ]);

      return el('div', { class: 'sf-step' }, [
        el('h2', {
          class: 'sf-step__title',
          tabindex: '-1',
          text: 'How would you like to find your shade?',
        }),
        /*
          Say which product this is about, when she came from one.

          Without it the finder looks like it covers the whole range, she gets
          one shade back, and there is no way to tell whether it narrowed to
          what she was looking at or just picked something. One line removes
          that doubt.
        */
        this.scopedProduct
          ? el('p', { class: 'sf-scope' }, [
              el('span', { class: 'sf-scope__dot', 'aria-hidden': 'true' }),
              el('span', { text: 'Matching shades in ' }),
              el('strong', { text: this.scopedProduct }),
            ])
          : null,
        el('p', {
          class: 'sf-step__subtitle',
          text: 'Both take under a minute. The photo is more accurate because it measures your skin instead of asking you to guess.',
        }),

        el('div', { class: 'sf-modes' }, [
          card(
            ICONS.camera,
            'Most accurate',
            'Use a photo',
            'We read the colour of your skin and fill in the answers for you. You can change any of them after.',
            'About 20 seconds',
            () => { this.mode = 'photo'; this.render(); },
            this.settings.routePhoto || this.settings.photoExample
          ),
          card(
            ICONS.list,
            null,
            'Answer questions',
            `${this.questions.length} short questions. No camera needed.`,
            'About a minute',
            () => { this.mode = 'quiz'; this.step = 0; this.render(); }
          ),
        ]),

        el('div', { class: 'sf-note' }, [
          el('span', { 'aria-hidden': 'true', html: ICONS.lock }),
          el('span', {
            text: 'Your photo is read on your own phone. We only send a few tiny crops of skin, never a photo of your face.',
          }),
        ]),

        this.renderHistory(),
      ]);
    }

    /** Past matches, kept in this browser. Shown on the opening screen. */
    renderHistory() {
      const list = readHistory(this.family);
      if (!list.length) return null;

      const items = list.map((e) =>
        el('li', { class: 'sf-history__item' }, [
          el('span', {
            class: 'sf-history__swatch',
            style: e.image ? { backgroundImage: `url(${e.image})` } : { background: e.hex || '#ddd' },
            'aria-hidden': 'true',
          }),
          el('span', { class: 'sf-history__text' }, [
            el('strong', { text: e.name }),
            el('span', {
              class: 'sf-history__meta',
              text: timeAgo(e.ts) + ' · ' + (e.method === 'photo' ? 'from a photo' : 'from your answers'),
            }),
          ]),
          e.productUrl
            ? el('a', { class: 'sf-history__link', href: e.productUrl, text: 'View' })
            : null,
        ])
      );

      return el('div', { class: 'sf-history' }, [
        el('div', { class: 'sf-history__head' }, [
          el('p', { class: 'sf-history__title', text: 'Your past matches' }),
          el('button', {
            type: 'button',
            class: 'sf-history__clear',
            text: 'Clear',
            onClick: () => { clearHistory(this.family); this.render(); },
          }),
        ]),
        el('ul', { class: 'sf-history__list' }, items),
        el('p', {
          class: 'sf-history__note',
          text: 'Saved on this device only, so they will not show up on your phone if you did this on a laptop.',
        }),
      ]);
    }

    // ---- Question --------------------------------------------------------
    renderQuestion(question) {
      const selected = this.answers[question.key];
      const isMulti = question.inputType === 'multi';

      /*
        Real photographs, when the shop has uploaded them, get a portrait tile
        rather than a colour dot. A face is what a customer actually compares
        herself against, so it deserves the room.
      */
      const hasPhotos = (question.options || []).some((o) => o.image);

      /*
        With no photographs, the depth ladder still gets the big tile.

        It used to get a drawn portrait here, generated from the shade colour: a
        face, hair, eyes, the lot. The client's verdict on those was blunt and
        correct - a cartoon face on a beauty site reads as a placeholder someone
        forgot to replace, and six of them read as six placeholders.

        What survives is the geometry. The tile stays a large 3:4 field, the
        proportions of a shade card, and it is filled with the skin colour
        itself. A flat, accurate colour is honest about what it is: a colour to
        hold your jaw against. A drawn face pretends to be a person and is not.
      */
      const isDepthLadder = (question.options || []).every(
        (o) => o.scores && typeof o.scores.depth === 'number'
      );
      const toneTiles = !hasPhotos && isDepthLadder;

      const list = el('ul', {
        class: 'sf-options',
        'data-layout': hasPhotos ? 'image' : toneTiles ? 'tone' : question.layout || 'card',
        role: isMulti ? 'group' : 'radiogroup',
        'aria-label': question.title,
      });

      question.options.forEach((option) => {
        const isSelected = isMulti
          ? Array.isArray(selected) && selected.includes(option.value)
          : selected === option.value;

        /*
          The out-of-range answer, when the rest of the ladder is photographs.

          It gets a panel rather than a swatch. A flat colour tile sitting at the
          end of a row of five faces is the exact thing the all-or-nothing rule
          existed to prevent, and it also lies: it implies a sixth shade. This
          says what it actually is - past the end of what we make - in words.
        */
        const beyond =
          Boolean(option.scores && option.scores.out_of_range) &&
          !option.image &&
          (question.options || []).some((o) => o.image);

        const swatchStyle = option.image
          ? {
              backgroundImage: `url(${option.image})`,
              backgroundPosition: option.imageFocus || '50% 28%',
            }
          : beyond
          ? null
          : option.swatchHex
          ? { background: option.swatchHex }
          : null;

        const button = el(
          'button',
          {
            type: 'button',
            class: beyond ? 'sf-option sf-option--beyond' : 'sf-option',
            [isMulti ? 'aria-pressed' : 'aria-checked']: String(isSelected),
            role: isMulti ? undefined : 'radio',
            onClick: () => this.selectOption(question, option),
          },
          [
            swatchStyle ? el('span', { class: 'sf-option__swatch', style: swatchStyle, 'aria-hidden': 'true' }) : null,
            beyond
              ? el('span', { class: 'sf-option__beyond', 'aria-hidden': 'true' }, [
                  el('span', { class: 'sf-option__beyond-rule' }),
                  el('span', { class: 'sf-option__beyond-text', text: 'Past our range' }),
                ])
              : null,
            el('span', { class: 'sf-option__label', text: option.label }),
            option.helper ? el('span', { class: 'sf-option__helper', text: option.helper }) : null,
            el('span', { class: 'sf-option__check', 'aria-hidden': 'true', html: ICONS.check }),
          ]
        );

        list.appendChild(el('li', {}, [button]));
      });

      const canAdvance =
        question.optional || (isMulti ? Array.isArray(selected) && selected.length : selected != null);

      return el('div', { class: 'sf-step' }, [
        // A picture belonging to the question itself, when the shop has set one.
        question.hero
          ? el('div', {
              class: 'sf-step__hero',
              role: 'img',
              'aria-label': question.title,
              style: { backgroundImage: `url(${question.hero})` },
            })
          : null,
        el('h2', { class: 'sf-step__title', tabindex: '-1', text: question.title }),
        question.subtitle ? el('p', { class: 'sf-step__subtitle', text: question.subtitle }) : null,
        question.helpText ? this.renderHelp(question.helpText) : null,
        list,
        el('div', { class: 'sf-actions' }, [
          isMulti || question.optional
            ? el('button', {
                type: 'button',
                class: 'sf-btn sf-btn--primary',
                disabled: !canAdvance && !question.optional,
                text: question.optional && !canAdvance ? 'Skip' : 'Continue',
                onClick: () => this.next(),
              })
            : null,
          this.step > 0
            ? iconButton('Back', ICONS.back, { onClick: () => this.back() })
            : null,
        ]),
      ]);
    }

    renderHelp(text) {
      const body = el('div', { class: 'sf-help__body', text, hidden: true, id: 'sf-help-' + this.step });
      const toggle = el('button', {
        type: 'button',
        class: 'sf-help__toggle',
        text: 'How do I know?',
        'aria-expanded': 'false',
        'aria-controls': body.id,
        onClick: (e) => {
          const open = body.hidden;
          body.hidden = !open;
          e.currentTarget.setAttribute('aria-expanded', String(open));
          e.currentTarget.textContent = open ? 'Hide' : 'How do I know?';
        },
      });
      return el('div', { class: 'sf-help' }, [toggle, body]);
    }

    selectOption(question, option) {
      const isMulti = question.inputType === 'multi';

      if (isMulti) {
        const current = Array.isArray(this.answers[question.key]) ? this.answers[question.key] : [];
        this.answers[question.key] = current.includes(option.value)
          ? current.filter((v) => v !== option.value)
          : [...current, option.value];
        this.applyScores(option, false);
        this.render();
        return;
      }

      this.answers[question.key] = option.value;
      this.applyScores(option, true);

      // Single-select advances on tap. Small delay so the selected state is
      // visible before the step changes - otherwise it feels like a glitch.
      this.render();
      setTimeout(() => this.next(), 220);
    }

    /**
     * Pull scoring signals out of the option's `scores` JSON. Reserved keys
     * are stored on double-underscore properties so they never collide with
     * a question key the client invents later.
     */
    applyScores(option, exclusive) {
      const scores = option.scores || {};
      if (typeof scores.depth === 'number') this.answers.__depth = scores.depth;
      if (typeof scores.undertone === 'string') this.answers.__undertone = scores.undertone;
      if (scores.out_of_range) this.answers.__outOfRange = true;
      else if (exclusive && typeof scores.depth === 'number') this.answers.__outOfRange = false;
      if (scores.coverage) this.answers.__coverage = scores.coverage;
    }

    // ---- Photo step ------------------------------------------------------
    renderPhotoStep() {
      /*
        Two ways in, not one.

        This used to be a single input carrying capture="user", which on a phone
        means "open the selfie camera and nothing else". A customer with a photo
        already on her camera roll, in good light, taken by someone else, had no
        way to use it. The gallery input has no capture attribute, so the phone
        offers the camera roll and files; the camera input keeps it.
      */
      const onPick = (e) => {
        const file = e.target.files?.[0];
        if (file) this.handlePhoto(file);
      };

      const galleryInput = el('input', {
        type: 'file',
        accept: 'image/*',
        class: 'sf-visually-hidden',
        id: 'sf-photo-input',
        onChange: onPick,
      });

      // Only phones and tablets get the separate camera button. On a desktop
      // both buttons would open the same file dialog, which reads as a bug.
      const touch = typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;

      const cameraInput = touch
        ? el('input', {
            type: 'file',
            accept: 'image/*',
            capture: 'user',
            class: 'sf-visually-hidden',
            id: 'sf-camera-input',
            onChange: onPick,
          })
        : null;

      // A real <button> rather than a styled <label>, because a label cannot be
      // disabled - and the consent tick has to be able to gate it.
      const locked = Boolean(this.settings.requireConsent && !this.consent);

      const cameraBtn = cameraInput
        ? iconButton('Take a photo', ICONS.camera, {
            class: 'sf-btn sf-btn--primary',
            disabled: locked,
            onClick: () => cameraInput.click(),
          })
        : null;

      const uploadBtn = iconButton(
        cameraBtn ? 'Choose a saved photo' : 'Upload a photo',
        ICONS.image,
        {
          class: cameraBtn ? 'sf-btn sf-btn--secondary' : 'sf-btn sf-btn--primary',
          disabled: locked,
          onClick: () => galleryInput.click(),
        }
      );

      const gated = [cameraBtn, uploadBtn].filter(Boolean);

      const consentBox = this.settings.requireConsent
        ? el('label', { class: 'sf-consent' }, [
            el('input', {
              type: 'checkbox',
              checked: this.consent,
              onChange: (e) => {
                this.consent = e.target.checked;
                gated.forEach((b) => { b.disabled = !this.consent; });
              },
            }),
            el('span', { text: this.settings.consentText || 'I consent to my photo being used to find my shade.' }),
          ])
        : null;

      return el('div', { class: 'sf-step' }, [
        el('h2', {
          class: 'sf-step__title',
          tabindex: '-1',
          text: this.settings.photoIntroTitle || 'Add a photo',
        }),
        el('p', {
          class: 'sf-step__subtitle',
          text: this.settings.photoIntroBody || '',
        }),

        el('div', { class: 'sf-photo' }, [
          this.settings.photoExample
            ? el('figure', { class: 'sf-photo__example' }, [
                el('img', {
                  src: this.settings.photoExample,
                  alt: 'An example of a photo that reads well: even daylight on the face, nothing covering it.',
                  loading: 'lazy',
                  decoding: 'async',
                  style: { objectPosition: this.settings.photoExampleFocus || '50% 30%' },
                }),
                el('figcaption', { text: 'Aim for something like this: daylight on your face, hair back, nothing covering it, no filter.' }),
              ])
            : el('div', { class: 'sf-guide' },
                PHOTO_GUIDE.map((g) =>
                  el('div', { class: 'sf-guide__item', 'data-ok': String(g.ok) }, [
                    el('span', { class: 'sf-guide__art', 'aria-hidden': 'true', html: g.art }),
                    el('span', { class: 'sf-guide__label' }, [
                      el('span', { class: 'sf-guide__mark', 'aria-hidden': 'true', html: g.ok ? ICONS.check : ICONS.cross }),
                      el('span', { text: g.label }),
                    ]),
                  ])
                )
              ),
          el('div', { class: 'sf-photo__hint' }, [
            el('span', { 'aria-hidden': 'true', html: ICONS.lock }),
            el('span', {
              text:
                'Your photo is read on your own phone. We only send a few tiny crops of skin, never a photo of your face.',
            }),
          ]),
          consentBox,
          el('div', { class: 'sf-photo__dropzone' }, [
            galleryInput,
            cameraInput,
            ...gated,
            el('p', {
              class: 'sf-photo__aside',
              text: cameraBtn
                ? 'A photo someone else took of you in daylight often reads better than a selfie.'
                : 'Daylight, no filter, and nothing covering your face.',
            }),
          ]),
        ]),

        el('div', { class: 'sf-actions' }, [
          this.mode === 'photo'
            ? el('button', {
                type: 'button',
                class: 'sf-btn sf-btn--secondary',
                text: 'Answer questions instead',
                onClick: () => { this.mode = 'quiz'; this.step = 0; this.render(); },
              })
            : this.settings.photoOptional
            ? iconButton('Skip and use my answers', ICONS.list, {
                class: 'sf-btn sf-btn--secondary',
                onClick: () => this.finish(),
              })
            : null,
          iconButton('Back', ICONS.back, { onClick: () => this.back() }),
        ]),
      ]);
    }

    async handlePhoto(file) {
      this.showLoading('Reading your skin');

      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const img = new Image();
        img.decoding = 'async';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = dataUrl;
        });

        const analysis = await analyseImage(img);

        if (!analysis.ok) {
          this.showPhotoError(analysis.reason, analysis);
          return;
        }

        this.analysis = analysis;
        this.patchStrip = buildPatchStrip(analysis);
        this.photoDataUrl = this.consent ? buildStorablePhoto(img) : null;

        // Fill in anything the customer hasn't answered themselves. In photo
        // mode that's everything; in quiz mode their own answers stand.
        const read = deriveFromLab(analysis.lab, this.shades);
        this.reading = read;

        if (this.answers.__depth == null && read.depth != null) {
          this.answers.__depth = read.depth;
          this.derivedDepth = true;
        }
        if (!this.answers.__undertone || this.answers.__undertone === 'unknown') {
          this.answers.__undertone = read.undertone;
          this.derivedUndertone = true;
        }

        /*
          A photo goes straight to the match. No swatch gate in between.

          There was a step here that asked her to confirm what we had read
          before we would name a shade, and on paper it was the careful thing to
          do. In practice it made a customer do the tool's job: she uploaded a
          photo precisely so she would not have to compare herself against six
          swatches, and we answered by showing her six swatches. Worse, it fired
          on every photo, including the well-lit ones we had no doubts about.

          The correction it existed to provide has not gone anywhere. It lives
          on the result screen, next to the shade, as "I'm lighter" / "I'm
          deeper" - one tap, same effect, and only taken by the customers who
          actually disagree. When the light was poor we say so there too, in
          plain words, above the correction that answers it.
        */
        this.finish();
      } catch (err) {
        console.error('[ShadeFinder] Photo analysis failed:', err);
        this.showPhotoError('processing_failed');
      }
    }

    showPhotoError(reason, detail) {
      const messages = {
        no_face: "We could not find a face in that photo. Try again with your face in the middle and good light on it.",
        insufficient_skin:
          "We could not get a clean read on your skin. Hair, glasses or shadows may be in the way. Try pulling your hair back and facing a window.",
        multiple_faces:
          'There is more than one person in this photo, so we have not read a shade from it. Upload a photo of just you and we will try again.',
        uneven_light:
          'The light is falling very unevenly across your face in this photo, so any shade we named would be a guess. Face a window straight on, keep your hands away from your face, and try once more.',
        processing_failed:
          "Something went wrong reading that photo. Try another one, or carry on with your answers.",
      };

      const titles = {
        multiple_faces: 'There is more than one person here',
        uneven_light: 'The light on your face is too uneven',
      };

      /*
        Three things make a photo unmeasurable rather than merely awkward: more
        than one face, light so uneven that the samples disagree, and no view of
        the eyes to judge the light by. In all three we refuse instead of
        averaging our way to a confident wrong answer, and point at the swatch
        questions, which always work.
      */
      const refuse = reason === 'multiple_faces';

      const heading = titles[reason]
        ? el('p', {
            class: 'sf-photo__count',
            text:
              reason === 'multiple_faces' && detail && detail.faceCount > 1
                ? `We spotted ${detail.faceCount} faces in that photo.`
                : titles[reason],
          })
        : null;

      const actions = [
        iconButton('Try another photo', ICONS.redo, {
          class: refuse ? 'sf-btn sf-btn--primary' : 'sf-btn sf-btn--secondary',
          onClick: () => this.render(),
        }),
      ];

      // A refusal must never be a dead end. The swatch questions do not depend
      // on the light in the room, so they are always the way out.
      if (refuse) {
        actions.push(
          iconButton('Answer the questions instead', ICONS.list, {
            class: 'sf-btn sf-btn--ghost',
            onClick: () => {
              this.analysis = null;
              this.reading = null;
              this.result = null;
              this.mode = 'quiz';
              this.step = 0;
              this.derivedDepth = false;
              this.derivedUndertone = false;
              this.depthOverridden = false;
              this.render();
            },
          })
        );
      }

      if (!refuse) {
        actions.push(
          iconButton('Continue without a photo', ICONS.list, {
            class: 'sf-btn sf-btn--primary',
            onClick: () => this.finish(),
          })
        );
      }

      this.stage.innerHTML = '';
      this.stage.appendChild(
        el('div', { class: 'sf-step' }, [
          heading,
          el('div', { class: 'sf-photo__hint', 'data-tone': 'warn' }, [
            el('span', { 'aria-hidden': 'true', html: ICONS.alert }),
            el('span', { text: messages[reason] || messages.processing_failed }),
          ]),
          el('div', { class: 'sf-actions', style: { marginTop: '1.25rem' } }, actions),
        ])
      );
    }

    showLoading(label) {
      this.stage.innerHTML = '';
      this.stage.appendChild(
        el('div', { class: 'sf-loading' }, [
          el('div', { class: 'sf-spinner', role: 'status', 'aria-label': label }),
          el('p', { class: 'sf-loading__label', text: label }),
        ])
      );
    }

    // ---- Finish ----------------------------------------------------------
    /**
     * The customer corrects the depth we read off their photo.
     *
     * Their eyes beat our arithmetic. Once they have told us the photo read too
     * dark or too light, the photo's lightness is dropped from the match
     * entirely rather than argued with, and only its undertone is kept. A
     * measurement that has been overruled should not still be pulling.
     */
    adjustDepth(delta) {
      const depths = this.shades.map((s) => s.depthIndex).filter((d) => d > 0);
      if (!depths.length) return;

      const low = Math.min(...depths);
      const high = Math.max(...depths);
      const current = this.answers.__depth ?? this.reading?.depth ?? null;
      if (current == null) return;

      const next = Math.min(high, Math.max(low, current + delta));
      if (next === current) return;

      this.answers.__depth = next;
      this.answers.__outOfRange = false;
      this.depthOverridden = true;
      this.derivedDepth = false;
      this.finish();
    }

    /**
     * The best shade outside the scoped product, when the scoped one runs out.
     * Returns null unless the finder is scoped, the result is at or past an end
     * of its range, and another product genuinely goes further that way.
     */
    crossRangeFor(result, analysis) {
      if (!this.scopedProduct || !(result.outOfRange || result.edge)) return null;
      const inScope = new Set(this.shades);
      // Same kind of product only. Someone matching a foundation is never
      // pointed at a concealer because it happens to be numbered deeper.
      const families = new Set(this.shades.map((sh) => sh.family || ''));
      const others = (this.allShades || []).filter(
        (sh) => !inScope.has(sh) && families.has(sh.family || '')
      );
      if (!others.length) return null;

      const rungs = this.shades.map((sh) => sh.depthIndex).filter((d) => d > 0);
      if (!rungs.length) return null;
      const deeper = result.outOfRange || result.edge === 'deeper';
      const limit = deeper ? Math.max(...rungs) : Math.min(...rungs);

      // Only products that actually extend past this range, in her direction.
      const beyond = others.filter((sh) =>
        sh.depthIndex > 0 && (deeper ? sh.depthIndex > limit : sh.depthIndex < limit)
      );
      if (!beyond.length) return null;
      const products = new Set(beyond.map((sh) => sh.productHandle || sh.productUrl));
      const pool = others.filter((sh) => products.has(sh.productHandle || sh.productUrl));

      const alt = match(pool, { ...this.answers, __outOfRange: false }, analysis);
      if (!alt || alt.outOfRange) return null;
      const d = alt.top.shade.depthIndex;
      if (!(deeper ? d > limit : d < limit)) return null;

      return { result: alt, from: this.scopedProduct };
    }

    async finish() {
      this.showLoading('Finding your match');

      // A depth the customer has corrected by hand replaces the photo's, so the
      // photo is no longer allowed a vote on how deep the shade should be.
      const analysis = this.depthOverridden ? null : this.analysis;
      let result = match(this.shades, this.answers, analysis);

      if (!result) {
        this.stage.innerHTML = '';
        this.stage.appendChild(
          el('div', { class: 'sf-photo__hint', 'data-tone': 'error' }, [
            'No shades are configured for this finder yet.',
          ])
        );
        return;
      }

      /*
        Arrived from one product, and that product cannot serve her.

        "We don't have your shade yet" is only true of the shop, never of one
        bottle. Scoped to Glow Burst, a deep face either falls off the end of
        the range or lands on its deepest shade by default - while Daily
        Sunbath, one click away, has her rung. So when the scoped range runs
        out, look across the rest of the table before saying anything.

        Only ever towards the side she ran off: a deeper face is pointed at a
        deeper shade, never sideways to a product that merely scores similarly.
      */
      const cross = this.crossRangeFor(result, analysis);
      if (cross && result.outOfRange) {
        // Nothing here fits her, and something elsewhere does. Lead with that.
        result = { ...cross.result, crossFrom: cross.from, crossReason: 'range' };
      } else if (cross) {
        result.crossTo = cross.result.top.shade;
        result.crossFrom = cross.from;
      }

      this.result = result;

      if (!result.outOfRange) {
        writeHistory({
          handle: result.top.shade.handle,
          name: result.top.shade.name,
          hex: result.top.shade.swatchHex,
          image: result.top.shade.image,
          productUrl: result.top.shade.productUrl,
          variantId: result.top.shade.variantId,
          confidence: result.confidence,
          method: this.analysis?.ok ? (this.depthOverridden ? 'photo, corrected' : 'photo') : 'answers',
          sessionId: this.sessionId,
          ts: Date.now(),
        }, this.family);
      }

      // Fire the API call. We do NOT await the copy before painting the result -
      // the deterministic match is already final. If the model comes back in
      // time we enrich the screen; if not, nothing is lost.
      const apiPromise = callApi(this.endpoint, {
        sessionId: this.sessionId,
        answers: this.answers,
        method: this.analysis?.ok ? (this.depthOverridden ? 'corrected' : 'hybrid') : 'quiz',
        measured: this.analysis?.ok
          ? {
              lab: this.analysis.lab,
              regions: Object.fromEntries(
                Object.entries(this.analysis.regions).map(([k, v]) => [k, v.lab])
              ),
              frameL: this.analysis.frameL,
              spread: this.analysis.spread,
              uncertain: this.analysis.uncertain,
              shadowedRegions: this.analysis.shadowedRegions,
              quality: this.analysis.quality,
              depthOverridden: Boolean(this.depthOverridden),
            }
          : null,
        patchImage: this.analysis?.ok ? this.patchStrip : null,
        photo: this.photoDataUrl,
        consent: this.consent,
        candidates: result.ranked.slice(0, 3).map((r) => ({
          handle: r.shade.handle,
          name: r.shade.name,
          lab: normaliseLab(r.shade),
          deltaE: r.deltaE,
          score: r.score,
        })),
        recommended: result.top.shade.handle,
        alternate: result.runnerUp?.shade.handle || null,
        confidence: result.confidence,
        deltaE: result.top.deltaE,
        outOfRange: result.outOfRange,
        durationMs: Date.now() - this.startedAt,
        source: location.pathname,
      });

      if (result.outOfRange) this.renderNoMatch();
      else this.renderResult(result);

      const api = await apiPromise;
      if (api && !result.outOfRange) this.enrichResult(api);
    }

    renderNoMatch() {
      this.progress.hidden = true;
      this.stage.innerHTML = '';

      this.stage.appendChild(
        el('div', { class: 'sf-step sf-nomatch' }, [
          el('h2', {
            class: 'sf-nomatch__title',
            tabindex: '-1',
            text: this.settings.noMatchTitle || "We don't have your shade yet",
          }),
          el('p', { class: 'sf-nomatch__body', text: this.settings.noMatchBody || '' }),
          this.renderEmailCapture('Notify me when my shade launches', 'waitlist'),
        ])
      );
    }

    /**
     * The one line that explains a match from outside the product she came
     * from, or a match at the end of it. Nothing at all in the ordinary case.
     */
    renderCrossNote(result) {
      if (!result.crossFrom) return null;
      const shade = result.top.shade;

      if (result.crossReason === 'range') {
        return el('div', { class: 'sf-cross', role: 'note' }, [
          el('span', { class: 'sf-cross__icon', 'aria-hidden': 'true', html: ICONS.sparkle }),
          el('p', { class: 'sf-cross__text' }, [
            el('strong', { text: result.crossFrom }),
            ' does not go deep enough for you yet. ',
            el('strong', { text: shade.productTitle || 'Another of our products' }),
            ' does, and this is your shade in it.',
          ]),
        ]);
      }

      const to = result.crossTo;
      if (!to) return null;
      const here = shade.name;
      const which = result.edge === 'lighter' ? 'lightest' : 'deepest';
      const more = result.edge === 'lighter' ? 'lighter' : 'deeper';
      const href = to.productUrl
        ? to.productUrl + (to.variantId ? (to.productUrl.includes('?') ? '&' : '?') + 'variant=' + to.variantId : '')
        : null;

      return el('div', { class: 'sf-cross', role: 'note' }, [
        el('span', { class: 'sf-cross__icon', 'aria-hidden': 'true', html: ICONS.sparkle }),
        el('p', { class: 'sf-cross__text' }, [
          `${here} is the ${which} shade of `,
          el('strong', { text: result.crossFrom }),
          `, and you may be a little ${more}. `,
          el('strong', { text: to.productTitle || 'Another of our products' }),
          ` goes further: ${to.name}.`,
        ]),
        href
          ? el('a', { class: 'sf-btn sf-btn--secondary sf-btn--small sf-cross__link', href, text: `See ${to.name}` })
          : null,
      ]);
    }

    renderResult(result) {
      this.progress.hidden = true;
      this.stage.innerHTML = '';

      const shade = result.top.shade;
      const alt = result.runnerUp?.shade;
      const confidencePct = Math.round(result.confidence * 100);

      const confidenceColour =
        result.confidence > 0.7
          ? 'var(--sf-good)'
          : result.confidence > 0.45
          ? 'var(--sf-warn)'
          : 'var(--sf-bad)';

      /*
        At the end of a range there is no "next shade" to be close to, so the
        usual middle line would be false. Say what is actually true.
      */
      const confidenceLabel =
        result.edge === 'deeper'
          ? 'Our deepest shade here. You may be a shade deeper still.'
          : result.edge === 'lighter'
          ? 'Our lightest shade here. You may be a shade lighter still.'
          : result.confidence > 0.7
          ? "We are confident about this one."
          : result.confidence > 0.45
          ? "Good match. You are close to the next shade too."
          : "This is our best guess. You are sitting between two shades.";

      // Show the product itself when there is a photo of it. A flat colour
      // block is only a stand-in for a shade that has not been linked to a
      // product yet.
      const heroArt = shade.productImage || shade.image;
      const swatchStyle = heroArt
        ? { backgroundImage: `url(${heroArt})` }
        : { background: shade.swatchHex || '#ddd' };

      const hero = el('div', { class: 'sf-result__hero' }, [
        el('div', {
          class: 'sf-result__swatch',
          'data-kind': heroArt ? 'photo' : 'swatch',
          style: swatchStyle,
          'aria-hidden': 'true',
        }, [
          // Same reasoning as the range cards: the photo identifies the
          // product, the chip identifies the shade.
          heroArt ? el('span', { class: 'sf-result__chip', style: { background: shade.swatchHex || '#ddd' } }) : null,
        ]),
        el('div', {}, [
          el('p', { class: 'sf-result__eyebrow', text: 'Your match' }),
          el('h2', { class: 'sf-result__name', tabindex: '-1', text: shade.headline || shade.name }),
          shade.productTitle
            ? el('p', { class: 'sf-result__product' }, [
                el('span', { text: shade.productTitle }),
                shade.variantTitle && shade.variantTitle !== shade.name
                  ? el('span', { class: 'sf-result__variant', text: shade.variantTitle })
                  : null,
              ])
            : null,

          el('div', { class: 'sf-confidence' }, [
            el('div', { class: 'sf-confidence__track' }, [
              el('div', {
                class: 'sf-confidence__bar',
                style: { width: confidencePct + '%', background: confidenceColour },
              }),
            ]),
            el('span', { class: 'sf-confidence__label', text: confidenceLabel }),
          ]),

          el('p', { class: 'sf-result__body', 'data-sf-description': '', text: shade.description || '' }),

          shade.price != null
            ? el('p', {
                class: 'sf-result__price',
                text: formatMoney(shade.price, this.config.moneyFormat),
              })
            : null,

          el('div', { class: 'sf-actions' }, [
            shade.variantId && shade.available
              ? el('button', {
                  type: 'button',
                  class: 'sf-btn sf-btn--primary',
                  text: shade.ctaLabel || 'Add to bag',
                  onClick: (e) => this.addToCart(shade, e.currentTarget),
                })
              : shade.productUrl
              ? el('a', { class: 'sf-btn sf-btn--primary', href: shade.productUrl, text: 'View product' })
              : null,
            shade.productUrl
              ? el('a', { class: 'sf-btn sf-btn--ghost', href: shade.productUrl, text: 'See full details' })
              : null,
          ]),
        ]),
      ]);

      const parts = [];
      const crossNote = this.renderCrossNote(result);
      if (crossNote) parts.push(crossNote);
      parts.push(hero);

      const readout = this.renderReadout();
      if (readout) parts.push(readout);

      const panel = this.renderDebug(result);
      if (panel) parts.push(panel);

      // The quiz asked people to judge their own skin against six circles on a
      // screen, which is the least reliable part of the whole flow. Offer to
      // check it properly now that they have something to compare against.
      if (this.settings.photoEnabled && !this.analysis?.ok) {
        parts.push(
          el('div', { class: 'sf-upgrade' }, [
            el('span', { class: 'sf-upgrade__icon', 'aria-hidden': 'true', html: ICONS.sparkle }),
            el('div', { class: 'sf-upgrade__text' }, [
              el('p', { class: 'sf-upgrade__title', text: 'Want to be sure?' }),
              el('p', {
                class: 'sf-upgrade__note',
                text: 'Add a photo and we will measure the actual colour of your skin, then check it against what you told us. It takes about twenty seconds.',
              }),
            ]),
            el('button', {
              type: 'button',
              class: 'sf-btn sf-btn--secondary',
              text: 'Add a photo',
              onClick: () => {
                this.mode = 'photo';
                this.step = 0;
                this.render();
              },
            }),
          ])
        );
      }

      if (alt) {
        parts.push(
          el('div', { class: 'sf-alt' }, [
            el('div', {
              class: 'sf-alt__swatch',
              style: alt.image ? { backgroundImage: `url(${alt.image})` } : { background: alt.swatchHex || '#ddd' },
              'aria-hidden': 'true',
            }),
            el('div', { class: 'sf-alt__text' }, [
              el('p', { class: 'sf-alt__title', text: `Also worth trying: ${alt.name}` }),
              el('p', {
                class: 'sf-alt__note',
                text: 'When you are unsure, take the lighter one. Warming it up with bronzer is easy. Fixing a shade that is too dark is not.',
              }),
            ]),
            alt.productUrl ? el('a', { class: 'sf-btn sf-btn--secondary', href: alt.productUrl, text: 'View' }) : null,
          ])
        );
      }

      /*
        Full range strip, with the match highlighted.

        The range of the product she was matched in, not every shade in the
        table. With two products that is 5 or 18 cards instead of 23, and the
        cards next to her match are shades she could actually swap to in the
        same bottle rather than a different product that happens to sort nearby.
        Shades not yet linked to a product have no family to group by, so they
        fall back to whatever the finder was showing.
      */
      const rangeShades = shade.productHandle
        ? (this.allShades || this.shades).filter((s) => s.productHandle === shade.productHandle)
        : this.shades;
      parts.push(
        el('div', { class: 'sf-range' }, [
          el('div', { class: 'sf-range__head' }, [
            el('p', { class: 'sf-range__title', text: 'The full range' }),
            el('p', {
              class: 'sf-range__note',
              text: shade.productTitle
                ? `Every shade of ${shade.productTitle}. Your match is marked.`
                : 'Your match is marked. You can buy any of them from here.',
            }),
          ]),
          el(
            'ul',
            { class: 'sf-range__list' },
            rangeShades.map((s) => this.renderProductCard(s, s.handle === shade.handle))
          ),
        ])
      );

      if (this.settings.captureEmail) {
        parts.push(this.renderEmailCapture(this.settings.emailPrompt || 'Email me my match', 'result'));
      }

      if (this.settings.resultDisclaimer) {
        parts.push(el('p', { class: 'shade-finder__footnote', text: this.settings.resultDisclaimer }));
      }

      parts.push(
        el('div', { class: 'sf-actions' }, [
          iconButton('Start over', ICONS.redo, { onClick: () => this.reset() }),
        ])
      );

      this.stage.appendChild(el('div', { class: 'sf-step sf-result' }, parts));
    }

    /**
     * One product card.
     *
     * Falls back gracefully at every level, because a shade that has been
     * created in the admin but not yet linked to a product is a normal state
     * during setup, and the card must not collapse or throw when that happens:
     *
     *   product photo -> result image -> flat swatch colour
     *   add to bag    -> view product  -> nothing
     */
    renderProductCard(s, isMatch) {
      const art = s.productImage || s.image;

      /*
        The photo and the swatch do different jobs, so the card carries both.

        On this store no variant has its own picture, which means all five
        foundation shades return the same product shot. A grid of five identical
        photos tells a customer nothing. The swatch sits on top of the photo so
        the card still says "this is the product" while the colour is what
        actually distinguishes one card from the next.
      */
      const media = el('span', {
        class: 'sf-card__media',
        'aria-hidden': 'true',
        'data-kind': art ? 'photo' : 'swatch',
        style: art ? { backgroundImage: `url(${art})` } : { background: s.swatchHex || '#ddd' },
      }, [
        art ? el('span', { class: 'sf-card__chip', style: { background: s.swatchHex || '#ddd' } }) : null,
      ]);

      const soldOut = Boolean(s.variantId) && !s.available;

      return el('li', { class: 'sf-card', 'data-current': String(Boolean(isMatch)) }, [
        isMatch ? el('span', { class: 'sf-card__flag', text: 'Your match' }) : null,
        soldOut ? el('span', { class: 'sf-card__flag sf-card__flag--muted', text: 'Sold out' }) : null,

        s.productUrl
          ? el('a', { class: 'sf-card__link', href: s.productUrl, 'aria-label': `View ${s.name}` }, [media])
          : media,

        el('div', { class: 'sf-card__body' }, [
          // Only repeat the swatch beside the name when the photo could not
          // carry it, otherwise the same dot appears twice on one card.
          art ? null : el('span', { class: 'sf-card__swatch', 'aria-hidden': 'true', style: { background: s.swatchHex || '#ddd' } }),
          el('div', { class: 'sf-card__text' }, [
            el('p', { class: 'sf-card__name', text: s.name }),
            s.price != null
              ? el('p', { class: 'sf-card__price' }, [
                  el('span', { text: formatMoney(s.price, this.config.moneyFormat) }),
                  s.compareAtPrice && s.compareAtPrice > s.price
                    ? el('s', { text: formatMoney(s.compareAtPrice, this.config.moneyFormat) })
                    : null,
                ])
              : null,
          ]),
        ]),

        s.variantId && s.available
          ? el('button', {
              type: 'button',
              class: 'sf-btn sf-btn--secondary sf-card__cta',
              text: s.ctaLabel || 'Add to bag',
              onClick: (e) => this.addToCart(s, e.currentTarget),
            })
          : s.productUrl
          ? el('a', { class: 'sf-btn sf-btn--secondary sf-card__cta', href: s.productUrl, text: 'View' })
          : null,
      ]);
    }

    /**
     * When the photo filled in the answers, show what was read and offer a way
     * to overrule it. Auto-filling without showing your working is how a tool
     * loses trust the first time it gets someone wrong.
     */
    renderReadout() {
      if (!this.analysis?.ok || !(this.derivedDepth || this.derivedUndertone || this.depthOverridden)) {
        return null;
      }

      const depthName =
        this.reading?.nearest?.name ||
        this.shades.find((s) => s.depthIndex === this.answers.__depth)?.name ||
        null;

      const chips = [];

      if (this.analysis.rgb) {
        chips.push(
          el('span', { class: 'sf-readout__chip' }, [
            el('i', { style: { background: rgbToHex(this.analysis.rgb) }, 'aria-hidden': 'true' }),
            el('span', { text: 'Your skin' }),
          ])
        );
      }
      if (this.derivedDepth && depthName) {
        chips.push(el('span', { class: 'sf-readout__chip', text: 'Depth: ' + depthName }));
      }
      if (this.derivedUndertone && this.answers.__undertone) {
        const tone = this.answers.__undertone;
        chips.push(el('span', { class: 'sf-readout__chip', text: 'Undertone: ' + tone.charAt(0).toUpperCase() + tone.slice(1) }));
      }

      /*
        A photo read is a measurement of a room as much as of a face. Even after
        the swatch check, a phone camera in bad light can land a step out, so
        the customer keeps the last word here too, in one tap, rather than
        having to start the whole thing again.
      */
      const depths = this.shades.map((s) => s.depthIndex).filter((d) => d > 0);
      const current = this.answers.__depth ?? null;
      const canLighten = current != null && depths.length && current > Math.min(...depths);
      const canDeepen = current != null && depths.length && current < Math.max(...depths);

      const correction = el('div', { class: 'sf-readout__fix' }, [
        el('span', {
          class: 'sf-readout__ask',
          text: this.depthOverridden ? 'You corrected this. Still not right?' : 'Does that look like your skin?',
        }),
        canLighten
          ? iconButton("I'm lighter", ICONS.minus, {
              class: 'sf-btn sf-btn--secondary sf-btn--small',
              onClick: () => this.adjustDepth(-1),
            })
          : null,
        canDeepen
          ? iconButton("I'm deeper", ICONS.plus, {
              class: 'sf-btn sf-btn--secondary sf-btn--small',
              onClick: () => this.adjustDepth(1),
            })
          : null,
      ]);

      /*
        The low-light notice.

        A notice, not a warning and not a gate: the match above it stands, and
        she can carry on and buy it without touching this. It says what happened
        and which way the error runs, because "dim light" on its own tells her
        nothing she can act on, whereas "this may read a step too dark" points
        her straight at the button underneath.
      */
      const lowLight = this.analysis.lowLight || this.analysis.uncertain
        ? el('p', { class: 'sf-readout__notice' }, [
            el('span', { class: 'sf-readout__notice-icon', 'aria-hidden': 'true', html: ICONS.bulb }),
            el('span', {
              text: this.analysis.lowLight
                ? 'The light in that photo was low. We still read it, but dim light makes skin look deeper than it is, so this may be a step too dark.'
                : 'The light fell unevenly across your face in that photo, so this reading is approximate.',
            }),
          ])
        : null;

      return el('div', { class: 'sf-readout' }, [
        el('span', { class: 'sf-readout__lead', text: 'What we read from your photo' }),
        ...chips,
        lowLight,
        correction,
        iconButton('Answer the questions instead', ICONS.list, {
          class: 'sf-btn sf-btn--ghost sf-btn--small sf-readout__restart',
          onClick: () => {
            this.result = null;
            this.mode = 'quiz';
            this.step = 0;
            this.answers.__depth = null;
            this.answers.__undertone = null;
            this.derivedDepth = false;
            this.derivedUndertone = false;
            this.depthOverridden = false;
            this.render();
          },
        }),
      ]);
    }

    /**
     * The numbers behind a reading, for calibration. Only on ?sfdebug=1.
     *
     * Comparing the measured L* here against the L* stored on the shade someone
     * actually wears is the only way to find out whether the shade table's
     * values sit where photographed skin sits. That comparison needs people
     * whose correct shade is already known, which is a shop-floor job, not a
     * code one, so this panel exists to make it a five-minute one.
     */
    renderDebug(result) {
      if (!this.debug) return null;

      const a = this.analysis;
      const lines = [];

      if (a?.ok) {
        lines.push(`measured Lab   L ${a.lab.L.toFixed(1)}  a ${a.lab.a.toFixed(1)}  b ${a.lab.b.toFixed(1)}`);
        lines.push(`light          frame L ${a.frameL == null ? '?' : a.frameL.toFixed(1)}  spread ${a.spread == null ? '?' : a.spread.toFixed(1)}${a.uncertain ? '  (uncertain)' : ''}`);
        lines.push(`regions used   ${a.usedRegions.join(', ')}${a.shadowedRegions.length ? `  (dropped: ${a.shadowedRegions.join(', ')})` : ''}`);
        lines.push(
          `region L*      ${Object.entries(a.regions).map(([k, v]) => `${k} ${v.lab.L.toFixed(1)}`).join('  ')}`
        );
        lines.push(`quality        ${a.quality.toFixed(2)}`);
      } else {
        lines.push('measured       no photo in this session');
      }

      if (result) {
        lines.push(
          `ranking        ${result.ranked
            .slice(0, 4)
            .map((r) => `${r.shade.name} ${(r.score * 100).toFixed(0)}%${r.deltaE == null ? '' : ` dE${r.deltaE.toFixed(1)}`}`)
            .join('  ')}`
        );
        lines.push(
          `shade table L* ${this.shades
            .map((s) => { const lab = normaliseLab(s); return lab ? `${s.name} ${lab.L}` : `${s.name} ?`; })
            .join('  ')}`
        );
        lines.push(`confidence     ${(result.confidence * 100).toFixed(0)}%`);
      }

      return el('div', { class: 'sf-debug' }, [
        el('p', { class: 'sf-debug__title', text: 'Calibration readout' }),
        el('pre', { class: 'sf-debug__body', text: lines.join('\n') }),
        el('p', {
          class: 'sf-debug__note',
          text: 'Only visible with sfdebug=1 in the address bar. Compare the measured L* with the shade table L* of the shade this person actually wears.',
        }),
      ]);
    }

    /** Swap in the model's copy once it arrives, if it returned any. */
    enrichResult(api) {
      if (api.description) {
        const node = this.stage.querySelector('[data-sf-description]');
        if (node) node.textContent = api.description;
      }
      if (api.photoAdvice) {
        const hero = this.stage.querySelector('.sf-result__hero');
        hero?.insertAdjacentElement(
          'afterend',
          el('div', { class: 'sf-photo__hint', 'data-tone': 'warn' }, [
            el('span', { 'aria-hidden': 'true', html: ICONS.bulb }),
            el('span', { text: api.photoAdvice }),
          ])
        );
      }
    }

    renderEmailCapture(promptText, context) {
      const input = el('input', {
        type: 'email',
        class: 'sf-email__input',
        placeholder: 'you@email.com',
        'aria-label': 'Email address',
        required: true,
      });

      const status = el('p', { class: 'sf-alt__note', role: 'status' });

      const form = el(
        'form',
        {
          class: 'sf-email',
          onSubmit: async (e) => {
            e.preventDefault();
            const email = input.value.trim();
            if (!email || !email.includes('@')) {
              status.textContent = 'That email does not look right.';
              return;
            }
            status.textContent = 'Saving…';
            await callApi(this.endpoint, {
              sessionId: this.sessionId,
              email,
              context,
              updateOnly: true,
            });
            form.innerHTML = '';
            form.appendChild(el('p', { class: 'sf-alt__title', text: 'Done. Check your inbox.' }));
          },
        },
        [
          el('p', { class: 'sf-alt__title', text: promptText }),
          el('div', { class: 'sf-email__row' }, [
            input,
            el('button', { type: 'submit', class: 'sf-btn sf-btn--primary', text: 'Send' }),
          ]),
          status,
        ]
      );

      return form;
    }

    async addToCart(shade, button) {
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const res = await fetch(this.config.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: shade.variantId,
                quantity: 1,
                properties: { _shade_finder: this.sessionId },
              },
            ],
          }),
        });

        if (!res.ok) throw new Error('cart add failed');

        button.textContent = 'Added ✓';

        // Let Prestige (and any drawer app) know the cart changed.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));

        setTimeout(() => {
          button.textContent = original;
          button.disabled = false;
        }, 2200);
      } catch (err) {
        console.error('[ShadeFinder] Add to cart failed:', err);
        button.textContent = 'Try again';
        button.disabled = false;
        if (shade.productUrl) window.location.href = shade.productUrl;
      }
    }

    reset() {
      this.answers = {};
      this.picks = {};
      this.step = 0;
      this.mode = this.settings.photoEnabled ? null : 'quiz';
      this.analysis = null;
      this.reading = null;
      this.derivedDepth = false;
      this.derivedUndertone = false;
      this.depthOverridden = false;
      this.result = null;
      this.photoDataUrl = null;
      this.patchStrip = null;
      this.consent = false;
      this.sessionId = uid();
      this.startedAt = Date.now();
      this.render();
    }
  }

  // ==========================================================================
  // Boot
  // ==========================================================================

  /**
   * Point the back link at the page she actually came from.
   *
   * The link already works without this - the section renders a real href
   * server-side. This only upgrades it when the referrer is a page on this same
   * shop, which is the common case: she tapped through from a product.
   *
   * Only same-origin referrers, and the value is never written anywhere but
   * href. An off-site referrer is left alone rather than echoed back into a
   * link on the page.
   */
  function wireBackLink(root) {
    const link = root.querySelector('[data-sf-back]');
    if (!link) return;

    // An explicit ?return_to= wins: it is what the product page's own button
    // sends, and unlike the referrer it survives a reload or a shared link.
    let target = safeReturnPath();
    if (!target) {
      let ref;
      try {
        ref = new URL(document.referrer);
      } catch {
        return;
      }
      if (ref.origin !== window.location.origin) return;
      target = ref.pathname + ref.search;
    }
    // Coming from the finder itself is not "back".
    if (target.split('?')[0] === window.location.pathname) return;
    link.setAttribute('href', target);

    const label = link.querySelector('[data-sf-back-label]');
    if (label && /\/products\//.test(target)) label.textContent = 'Back to product';
  }

  function init() {
    document.querySelectorAll('[data-shade-finder]').forEach((root) => {
      if (root.dataset.sfInitialised) return;
      root.dataset.sfInitialised = 'true';
      try {
        wireBackLink(root);
        new ShadeFinder(root);
      } catch (err) {
        console.error('[ShadeFinder] Failed to initialise:', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the theme editor re-renders the section.
  document.addEventListener('shopify:section:load', init);

  // Expose for debugging, for the automated test suite, and for any future PDP
  // integration. Everything here is a pure function or a read-only constant, so
  // nothing on this object can put the widget into a bad state.
  window.LaMiorShadeFinder = {
    rgbToLab,
    deltaE2000,
    analyseImage,
    countPeople,
    spreadOf,
    looksLikeSkin,
    frameBrightness,
    liftForDetection,
    ShadeFinder,
    match,
    deriveFromLab,
    depthForMeasuredL,
    PHOTO_DEPTH_ANCHORS,
    scoreFromQuiz,
    scoreFromPhoto,
    skinDistance,
    normaliseLab,
    formatMoney,
    wireBackLink,
    readHistory,
    clearHistory,
    defaults: { questions: DEFAULT_QUESTIONS, shades: DEFAULT_SHADES },
  };
})();